import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import config from '../config.js';
import SurplusAllocationModal from '../components/SurplusAllocationModal.jsx';
import { apiErrorMessage } from '../utils/apiError';
// Time & Materials (slice 1d): the info panel / refusal on a T&M order; no allocations there.
import TmJobEntryPanel, { tmJobEntryGate } from '../components/tm/TmJobEntryPanel.jsx';
import TmJobLoadHintsModal from '../components/tm/TmJobLoadHintsModal.jsx';
import { loadHintsFrom, readJsonQuietly } from '../utils/tmDefects';
import { isBadIdeaCustomer } from '../utils/badIdeaParts';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { incomingByLine, inProductionByPart, partSurplus, overCoverageText } from '../features/salesOrders/surplus';
import { shopToday } from '../utils/shopDate';

// Adjust this to the API base URL:
const API_BASE = config.API_BASE_URL;

// Helper: format decimal hours into "Xh Ym"
function formatHoursAndMinutes(decimalHours) {
  if (!decimalHours || decimalHours <= 0) {
    return "0h 0m";
  }
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}h ${minutes}m`;
}

// =================================================================================================
// COMPONENT: Assembly Requirements Dashboard
// Visualizes the "Shopping List" (BOM) vs what has been added to the job so far.
// =================================================================================================
function AssemblyRequirementsTable({ assemblyComponents, buildQty, addedParts }) {
  const statusRows = useMemo(() => {
    if (!assemblyComponents || assemblyComponents.length === 0) return [];

    return assemblyComponents.map(comp => {
      // How many of this specific part are needed for the TOTAL job?
      const needed = comp.quantity_per_assembly * buildQty;
      
      // How many have we added to the job queue so far?
      const currentFilled = addedParts
        .filter(item => item.part_id === comp.part_id)
        .reduce((sum, item) => sum + item.quantity_added, 0);

      const remaining = needed - currentFilled;
      // Status Logic
      const isComplete = remaining <= 0;
      const rowClass = isComplete ? "table-success" : "table-warning";

      return {
        ...comp,
        needed,
        currentFilled,
        remaining,
        isComplete,
        rowClass
      };
    });
  }, [assemblyComponents, buildQty, addedParts]);

  const isFullySatisfied = statusRows.length > 0 && statusRows.every(r => r.isComplete);

  return (
    <div className="card mb-4 shadow-sm">
      <div className={`card-header text-white d-flex justify-content-between align-items-center ${isFullySatisfied ? 'bg-success' : 'bg-info'}`}>
        <h5 className="mb-0">
          <i className="bi bi-list-check me-2"></i> 
          Assembly Requirements: Building {buildQty} Units
        </h5>
        {isFullySatisfied ? (
          <span className="badge bg-white text-success fs-6">✔ All Parts Staged</span>
        ) : (
          <span className="badge bg-warning text-dark fs-6">⚠ Parts Remaining</span>
        )}
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th style={{width: '40%'}}>Part</th>
                <th className="text-center">Qty Per Assy</th>
                <th className="text-center">Total Needed</th>
                <th className="text-center">Added to Job</th>
                <th className="text-center">Remaining</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {statusRows.map(row => (
                <tr key={row.part_id} className={row.rowClass}>
                  <td>
                    <strong>{row.product_id}</strong>
                    <br />
                    <small className="text-muted">{row.description}</small>
                  </td>
                  <td className="text-center">{row.quantity_per_assembly}</td>
                  <td className="text-center">{row.needed}</td>
                  <td className="text-center"><strong>{row.currentFilled}</strong></td>
                  <td className="text-center">{Math.max(0, row.remaining)}</td>
                  <td className="text-center">
                    {row.isComplete ? (
                      <span className="text-success fw-bold">✔ OK</span>
                    ) : (
                      <span className="text-danger fw-bold">{row.remaining} Short</span>
                    )}
                  </td>
                </tr>
              ))}
              {statusRows.length === 0 && (
                <tr><td colSpan="6" className="text-center text-muted">Loading assembly details...</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =================================================================================================
// COMPONENT: Unified Assembly Composer (Handles BOTH Sheet & Shape Stock)
// =================================================================================================
function UnifiedAssemblyComposer({ 
    jobId, 
    customerId, 
    productionPartList, 
    cutSheetList, 
    cutListList, 
    materialList, 
    targetAssembly, 
    buildQty, 
    recipeInfo 
}) {
    const [selectedPart, setSelectedPart] = useState(null);
    
    // Dynamic Inputs based on part type
    const [selectedCutSheet, setSelectedCutSheet] = useState(null);
    const [selectedCutList, setSelectedCutList] = useState(null);
    
    const [requestedQuantity, setRequestedQuantity] = useState("");
    
    // Unified list of added items
    const [addedItems, setAddedItems] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // 1. Load Assembly BOM for Dashboard
    const [assemblyComponents, setAssemblyComponents] = useState([]);
    
    useEffect(() => {
        if (targetAssembly?.assembly_id) {
            // Fetch BOM (same as before)
            fetch(`${API_BASE}/assemblies/${targetAssembly.assembly_id}/components`)
                .then(res => res.json())
                .then(data => {
                    const primaryPart = productionPartList.find(p => p.part_id === targetAssembly.primary_part_id);
                    const fullList = [...data];
                    if(primaryPart) {
                        fullList.unshift({
                            part_id: primaryPart.part_id,
                            product_id: primaryPart.product_id,
                            description: primaryPart.description,
                            quantity_per_assembly: targetAssembly.primary_part_quantity || 1
                        });
                    }
                    setAssemblyComponents(fullList);
                })
                .catch(err => console.error("Failed BOM load", err));
        }
    }, [targetAssembly, productionPartList]);

    // 2. Determine Mode based on Selected Part
    const isShapeStock = selectedPart?.stock_type === 'Shape Stock';

    // 3. Reset secondary selections when primary part changes
    useEffect(() => {
        setSelectedCutSheet(null);
        setSelectedCutList(null);
        setRequestedQuantity("");
    }, [selectedPart]);

    // 4. Handle Add (Routes to correct API endpoint)
    const handleAddItem = async () => {
        if (!selectedPart || !requestedQuantity) return;
        if (!isShapeStock && !selectedCutSheet) return;
        if (isShapeStock && !selectedCutList) return;

        setLoading(true);
        setError(null);

        try {
            const qty = parseInt(requestedQuantity, 10);
            let resultInfo = {};

            if (isShapeStock) {
                // Shape Stock API Call
                const payload = { part_id: selectedPart.part_id, cut_list_id: selectedCutList.cut_list_id, requested_quantity: qty };
                const res = await fetch(`${API_BASE}/jobs/${jobId}/add_cut_list`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error(await apiErrorMessage(res));
                const data = await res.json();
                resultInfo = { 
                    sourceName: selectedCutList.cut_list_name, 
                    type: "Cut List", 
                    unitsUsed: data.sticks_needed + " Sticks" 
                };
            } else {
                // Sheet Stock API Call
                const payload = { part_id: selectedPart.part_id, cut_sheet_id: selectedCutSheet.cut_sheet_id, requested_quantity: qty };
                const res = await fetch(`${API_BASE}/jobs/${jobId}/add_cut_sheet`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error(await apiErrorMessage(res));
                const data = await res.json();
                resultInfo = { 
                    sourceName: selectedCutSheet.cut_sheet_name, 
                    type: "Cut Sheet", 
                    unitsUsed: data.sheets_needed + " Sheets" 
                };
            }

            // Update UI State
            setAddedItems(prev => [...prev, {
                part_id: selectedPart.part_id,
                partName: selectedPart.product_id,
                quantity_added: qty,
                ...resultInfo
            }]);

            // Reset Form
            setSelectedPart(null);
            setRequestedQuantity("");

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // 5. Recipe Handler (Handles mixed types automatically)
    const handleApplyRecipe = async () => {
         if (!recipeInfo?.recipe_id) return;
         if(!window.confirm(`Auto-add ALL required sheets/lists (Mixed Stock) for ${buildQty} assemblies?`)) return;
         
         setLoading(true);
         try {
             const batchesNeeded = Math.ceil(buildQty / (recipeInfo.batch_size || 1));
             const res = await fetch(`${API_BASE}/jobs/${jobId}/apply_recipe`, {
                 method: 'POST',
                 headers: { 'Content-Type': 'application/json' },
                 body: JSON.stringify({ recipe_id: recipeInfo.recipe_id, batches: batchesNeeded })
             });
             if(!res.ok) throw new Error(await res.text());
             
             // Refresh progress to see what was added
             const progressRes = await fetch(`${API_BASE}/jobs/${jobId}/progress`);
             if (progressRes.ok) {
                 const data = await progressRes.json();
                 // Map tasks to our addedItems format for the UI
                 const newItems = data.tasks.flatMap(t => {
                     // Handle Multi-part cut lists or simple items
                     if (t.parts && t.parts.length > 0) {
                        return t.parts.map(p => ({
                             part_id: p.part_id,
                             partName: p.product_id || p.part_description || "Auto-Added",
                             quantity_added: t.quantity * (p.quantity_per_length || p.quantity_per_sheet || 1),
                             sourceName: t.cut_list_id ? `Cut List #${t.cut_list_id}` : `Cut Sheet #${t.cut_sheet_id}`,
                             type: t.cut_list_id ? "Cut List" : "Cut Sheet",
                             unitsUsed: t.cut_list_id ? `${t.quantity} Sticks` : `${t.sheet_required} Sheets`
                        }));
                     }
                     return [{
                        part_id: t.part_id,
                        partName: t.part_description || "Auto-Added",
                        quantity_added: t.quantity,
                        sourceName: t.cut_list_id ? `Cut List #${t.cut_list_id}` : `Cut Sheet #${t.cut_sheet_id}`,
                        type: t.cut_list_id ? "Cut List" : "Cut Sheet",
                        unitsUsed: t.cut_list_id ? `${t.quantity || 0} Sticks` : `${t.sheet_required || 0} Sheets`
                     }];
                 });
                 setAddedItems(newItems);
             }
             alert("Recipe applied successfully!");
         } catch(err) {
             setError(err.message);
         } finally {
             setLoading(false);
         }
    };

    return (
        <div className="card shadow-sm border-primary mt-3">
            <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Unified Assembly Composer (Job #{jobId})</h5>
            </div>
            <div className="card-body">
                {/* 1. Dashboard */}
                {targetAssembly && (
                    <AssemblyRequirementsTable 
                        assemblyComponents={assemblyComponents}
                        buildQty={buildQty}
                        addedParts={addedItems} // Now contains mixed types
                    />
                )}

                {/* 2. Recipe Button */}
                {recipeInfo && recipeInfo.has_recipe && (
                     <div className="alert alert-success d-flex justify-content-between align-items-center mt-3">
                        <span>
                            <i className="bi bi-magic me-2"></i> 
                            <strong>Recipe Available:</strong> {recipeInfo.recipe_name} (Yields {recipeInfo.batch_size}/batch)
                        </span>
                        <button className="btn btn-success btn-sm fw-bold" onClick={handleApplyRecipe} disabled={loading}>
                            {loading ? "Processing..." : "Auto-Fill All Parts"}
                        </button>
                    </div>
                )}

                <hr />
                
                {/* 3. Manual Entry Form */}
                <h6 className="fw-bold mb-3">Add Component Manually</h6>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="row g-3 align-items-end">
                    {/* A. Select Part */}
                    <div className="col-md-4">
                        <label className="form-label">1. Select Part</label>
                        <select 
                            className="form-select" 
                            value={selectedPart?.part_id || ""}
                            onChange={e => setSelectedPart(productionPartList.find(p => p.part_id === parseInt(e.target.value)))}
                        >
                            <option value="">-- Choose Component --</option>
                            {/* Filter parts by customer if needed */}
                            {productionPartList
                                .filter(p => !customerId || p.customer_id === parseInt(customerId))
                                .map(p => (
                                <option key={p.part_id} value={p.part_id}>
                                    {p.product_id} ({p.stock_type || "Sheet Stock"})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* B. Dynamic Source Selection (Sheet vs List) */}
                    <div className="col-md-4">
                        <label className="form-label">
                            2. Select {isShapeStock ? "Cut List" : "Cut Sheet"}
                        </label>
                        {isShapeStock ? (
                            <select 
                                className="form-select"
                                disabled={!selectedPart}
                                value={selectedCutList?.cut_list_id || ""}
                                onChange={e => setSelectedCutList(cutListList.find(cl => cl.cut_list_id === parseInt(e.target.value)))}
                            >
                                <option value="">-- Select Cut List --</option>
                                {cutListList
                                    .filter(cl => cl.parts?.some(p => p.part_id === selectedPart?.part_id))
                                    .map(cl => (
                                        <option key={cl.cut_list_id} value={cl.cut_list_id}>{cl.cut_list_name}</option>
                                    ))
                                }
                            </select>
                        ) : (
                            <select 
                                className="form-select"
                                disabled={!selectedPart}
                                value={selectedCutSheet?.cut_sheet_id || ""}
                                onChange={e => setSelectedCutSheet(cutSheetList.find(cs => cs.cut_sheet_id === parseInt(e.target.value)))}
                            >
                                <option value="">-- Select Cut Sheet --</option>
                                {cutSheetList
                                    .filter(cs => cs.parts?.some(p => p.part_id === selectedPart?.part_id))
                                    .map(cs => {
                                        let label = cs.cut_sheet_name;
                                        if (cs.is_current_revision === false) label += " [ARCHIVED]";
                                        return (
                                            <option key={cs.cut_sheet_id} value={cs.cut_sheet_id}>{label}</option>
                                        );
                                    })
                                }
                            </select>
                        )}
                    </div>

                    {/* C. Quantity & Action */}
                    <div className="col-md-2">
                        <label className="form-label">3. Qty Needed</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            value={requestedQuantity} 
                            onChange={e => setRequestedQuantity(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <button 
                            className="btn btn-primary w-100" 
                            disabled={!selectedPart || !requestedQuantity || loading}
                            onClick={handleAddItem}
                        >
                            + Add
                        </button>
                    </div>
                </div>
                
                {/* 4. Added Items Table */}
                {addedItems.length > 0 && (
                    <div className="mt-4">
                        <h6>Staged Items for Job:</h6>
                        <table className="table table-sm table-bordered">
                            <thead className="table-light">
                                <tr>
                                    <th>Part</th>
                                    <th>Source Type</th>
                                    <th>Source Name</th>
                                    <th>Qty Added</th>
                                    <th>Stock Used</th>
                                </tr>
                            </thead>
                            <tbody>
                                {addedItems.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.partName}</td>
                                        <td><span className={`badge ${item.type === 'Cut List' ? 'bg-info' : 'bg-secondary'}`}>{item.type}</span></td>
                                        <td>{item.sourceName}</td>
                                        <td>{item.quantity_added}</td>
                                        <td>{item.unitsUsed}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                <div className="mt-4 text-end">
                    <button className="btn btn-lg btn-success" onClick={() => navigate("/jobs")}>
                        Finish & View All Jobs
                    </button>
                </div>
            </div>
        </div>
    );
}

// =================================================================================================
// COMPONENT: Custom Cut Sheet Modal (for Bulk / SO Fulfillment mode)
// =================================================================================================
function CustomCutSheetModal({ show, onClose, onSubmit, bulkCustomerId, materialList, customerList, jobCategory, soDetails }) {
    const [allParts, setAllParts] = useState([]);
    const [chosenParts, setChosenParts] = useState([]);
    const [partAddCustomerId, setPartAddCustomerId] = useState(bulkCustomerId || "");
    const [partAddPartId, setPartAddPartId] = useState(null);
    const [partAddSearch, setPartAddSearch] = useState("");
    const [partAddQty, setPartAddQty] = useState(1);
    const [sheetsNeeded, setSheetsNeeded] = useState(1);
    const [laserMinutes, setLaserMinutes] = useState("");
    const [useCustomMaterial, setUseCustomMaterial] = useState(false);
    const [customMaterialDesc, setCustomMaterialDesc] = useState("");
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [ownerFilter, setOwnerFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [thicknessFilter, setThicknessFilter] = useState("");
    const [finishFilter, setFinishFilter] = useState("");
    const [cutSheetImage, setCutSheetImage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setChosenParts([]);
            setPartAddCustomerId(bulkCustomerId || "");
            setPartAddPartId(null);
            setPartAddSearch("");
            setPartAddQty(1);
            setSheetsNeeded(1);
            setLaserMinutes("");
            setUseCustomMaterial(false);
            setCustomMaterialDesc("");
            setSelectedMaterial(null);
            setOwnerFilter("");
            setTypeFilter("");
            setThicknessFilter("");
            setFinishFilter("");
            setCutSheetImage(null);
            setSubmitting(false);
            setError(null);
        }
    }, [show, bulkCustomerId]);

    // Load all parts
    useEffect(() => {
        if (show && allParts.length === 0) {
            fetch(`${API_BASE}/parts`).then(r => r.json()).then(setAllParts).catch(err => console.error("Failed to load parts", err));
        }
    }, [show, allParts.length]);

    // Build set of part_ids on the SO (if in SO fulfillment mode)
    const soPartIds = useMemo(() => {
        if (!soDetails || !soDetails.lines) return null;
        const ids = new Set();
        soDetails.lines.forEach(item => {
            if (item.type === 'item' && item.data?.part_id) ids.add(item.data.part_id);
            if (item.type === 'assembly_group' && item.components) {
                item.components.forEach(c => { if (c.part_id) ids.add(c.part_id); });
            }
        });
        return ids.size > 0 ? ids : null;
    }, [soDetails]);

    const filteredPartsForCustomer = useMemo(() => {
        if (!partAddCustomerId) return [];
        let parts = allParts.filter(p => p.customer_id === parseInt(partAddCustomerId, 10));
        // In SO mode, restrict to parts on the sales order
        if (soPartIds) {
            parts = parts.filter(p => soPartIds.has(p.part_id));
        }
        return parts;
    }, [partAddCustomerId, allParts, soPartIds]);

    const filterOptions = useMemo(() => {
        const owners = new Map(), types = new Set(), thicknesses = new Set(), finishes = new Set();
        const customerMap = new Map(customerList.map(c => [c.customer_id, c.name]));
        materialList.forEach(m => {
            if (m.material_category === (jobCategory || 'Sheet Stock')) {
                if (m.owner_customer_id && !owners.has(m.owner_customer_id)) owners.set(m.owner_customer_id, customerMap.get(m.owner_customer_id) || `ID: ${m.owner_customer_id}`);
                if (m.material_type) types.add(m.material_type);
                if (m.thickness) thicknesses.add(m.thickness);
                if (m.surface_finish) finishes.add(m.surface_finish);
            }
        });
        return {
            owners: Array.from(owners.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
            types: [...types].sort(), thicknesses: [...thicknesses].sort((a, b) => a.localeCompare(b, undefined, {numeric: true})), finishes: [...finishes].sort()
        };
    }, [materialList, customerList, jobCategory]);

    const filteredMaterials = useMemo(() => {
        return materialList.filter(m => {
            if (m.material_category !== (jobCategory || 'Sheet Stock')) return false;
            if (ownerFilter && m.owner_customer_id !== parseInt(ownerFilter, 10)) return false;
            if (typeFilter && m.material_type !== typeFilter) return false;
            if (thicknessFilter && m.thickness !== thicknessFilter) return false;
            if (finishFilter && m.surface_finish !== finishFilter) return false;
            return true;
        });
    }, [materialList, jobCategory, ownerFilter, typeFilter, thicknessFilter, finishFilter]);

    const handleAddPartToSheet = () => {
        const qNum = parseInt(partAddQty, 10);
        if (isNaN(qNum) || qNum < 1) return;
        const customer = customerList.find(c => c.customer_id === parseInt(partAddCustomerId, 10));
        const part = allParts.find(p => p.part_id === parseInt(partAddPartId, 10));
        if (!customer || !part) return;

        const newParts = [...chosenParts, { part, customer, quantity: qNum }];
        setChosenParts(newParts);

        // Auto-populate material filters from first part's attributes
        if (chosenParts.length === 0) {
            if (part.material_type) setTypeFilter(part.material_type);
            if (part.display_thickness || part.thickness) setThicknessFilter(part.display_thickness || part.thickness);
        }

        setPartAddPartId(null);
        setPartAddSearch("");
        setPartAddQty(1);
    };

    function checkAndSetFile(file) {
        const img = new Image();
        img.onload = () => {
            if (img.width > 1920 || img.height > 1080) {
                alert("Image is larger than 1920x1080. Please resize or pick a smaller screenshot."); return;
            }
            setCutSheetImage(file);
        };
        img.src = URL.createObjectURL(file);
    }

    function handlePaste(e) {
        if (e.clipboardData?.files?.length) {
            checkAndSetFile(e.clipboardData.files[0]);
            e.preventDefault();
        }
    }

    // Calculate preview
    const previewParts = chosenParts.map(cp => ({
        ...cp,
        totalQuantity: cp.quantity * sheetsNeeded
    }));

    const estimatedLaserHrs = useMemo(() => {
        if (laserMinutes && parseInt(laserMinutes, 10) > 0) {
            return (parseInt(laserMinutes, 10) / 60) * sheetsNeeded;
        }
        return 0;
    }, [laserMinutes, sheetsNeeded]);

    const handleSubmit = async () => {
        setError(null);
        if (chosenParts.length === 0) { setError("Add at least one part to the cut sheet."); return; }
        if (!useCustomMaterial && !selectedMaterial) { setError("Select a material or toggle to custom material."); return; }
        if (useCustomMaterial && !customMaterialDesc.trim()) { setError("Describe the non-inventory material."); return; }

        setSubmitting(true);
        try {
            // 1. Create temporary cut sheet
            const csBody = {
                cut_sheet_name: `CustomCS_Bulk_${Date.now()}`,
                description: "Temporary cut sheet for bulk custom job",
                is_temporary: true,
                active_status: false,
                notes: "Auto-generated for bulk custom job.",
                initial_est_laser_minutes: laserMinutes ? parseInt(laserMinutes, 10) : null,
                material_id: useCustomMaterial ? null : selectedMaterial.material_id,
                custom_material_description: useCustomMaterial ? customMaterialDesc : null,
            };
            const csRes = await fetch(`${API_BASE}/cut_sheets`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(csBody)
            });
            if (!csRes.ok) throw new Error(`Failed to create cut sheet: ${await csRes.text()}`);
            const csData = await csRes.json();

            // 2. Add parts to cut sheet
            for (const cp of chosenParts) {
                const cspBody = { part_id: cp.part.part_id, quantity_per_sheet: cp.quantity, customer_id: cp.customer.customer_id };
                const cspRes = await fetch(`${API_BASE}/cut_sheets/${csData.cut_sheet_id}/parts`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cspBody)
                });
                if (!cspRes.ok) throw new Error(`Failed to add part ${cp.part.product_id}: ${await cspRes.text()}`);
            }

            // 3. Upload image if provided
            if (cutSheetImage) {
                try {
                    const formData = new FormData();
                    formData.append("file", cutSheetImage);
                    await fetch(`${API_BASE}/cut_sheets/${csData.cut_sheet_id}/upload-image`, { method: "POST", body: formData });
                } catch (err) { console.warn("Image upload error:", err); }
            }

            // 4. Build the cutSheet object for the bulk row
            const primary = chosenParts[0];
            const qtyPerSheet = primary.quantity;
            const totalQty = qtyPerSheet * sheetsNeeded;

            const cutSheetObj = {
                cut_sheet_id: csData.cut_sheet_id,
                cut_sheet_name: csBody.cut_sheet_name,
                material_id: csBody.material_id,
                custom_material_description: csBody.custom_material_description,
                is_temporary: true,
                initial_est_laser_minutes: csBody.initial_est_laser_minutes,
                parts: chosenParts.map(cp => ({
                    part_id: cp.part.part_id,
                    quantity_per_sheet: cp.quantity,
                    customer_id: cp.customer.customer_id
                }))
            };

            const materialDesc = useCustomMaterial
                ? customMaterialDesc
                : (selectedMaterial ? `${selectedMaterial.material_code} - ${selectedMaterial.description}` : "");

            // 5. Build and return bulk row data
            const rowData = {
                id: Date.now(),
                part: primary.part,
                partSearch: primary.part.product_id,
                cutSheet: cutSheetObj,
                cutList: null,
                quantity: totalQty,
                tempQuantity: String(totalQty),
                jobPriority: 2,
                dueDate: shopToday(new Date(Date.now() + 7 * 86400000)), // a week out, the shop's (Central) day
                previewParts: chosenParts.map(cp => ({
                    part_id: cp.part.part_id,
                    product_id: cp.part.product_id,
                    description: cp.part.description,
                    totalQuantity: cp.quantity * sheetsNeeded
                })),
                sheetsNeeded: sheetsNeeded,
                sticksNeeded: 0,
                laserHrs: estimatedLaserHrs,
                error: null,
                isCustom: true,
                customCutSheetId: csData.cut_sheet_id,
                customMaterialDesc: materialDesc
            };

            onSubmit(rowData);
        } catch (err) {
            setError(err.message);
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onPaste={handlePaste}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-warning-subtle">
                        <h5 className="modal-title"><i className="bi bi-file-earmark-plus me-2"></i>Add Custom Cut Sheet</h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        {/* Material Toggle */}
                        <div className="form-check form-switch mb-3">
                            <input className="form-check-input" type="checkbox" id="customModalMaterialSwitch" checked={useCustomMaterial} onChange={(e) => { setUseCustomMaterial(e.target.checked); if (e.target.checked) setSelectedMaterial(null); }} />
                            <label className="form-check-label" htmlFor="customModalMaterialSwitch">Use a Non-Inventory or Customer-Supplied Material</label>
                        </div>

                        {useCustomMaterial ? (
                            <div className="mb-3">
                                <label>Describe the Material</label>
                                <input type="text" className="form-control" value={customMaterialDesc} onChange={(e) => setCustomMaterialDesc(e.target.value)} placeholder="e.g., Customer-supplied 2'x3' 1/8in acrylic" />
                            </div>
                        ) : (
                            <>
                                <div className="p-2 border rounded mb-3 bg-light">
                                    <label className="form-label fw-bold">Filter Materials</label>
                                    <div className="row g-2">
                                        <div className="col-md-3"><select className="form-select form-select-sm" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}><option value="">All Owners</option>{filterOptions.owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
                                        <div className="col-md-3"><select className="form-select form-select-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}><option value="">All Types</option>{filterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                                        <div className="col-md-3"><select className="form-select form-select-sm" value={thicknessFilter} onChange={e => setThicknessFilter(e.target.value)}><option value="">All Thicknesses</option>{filterOptions.thicknesses.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                                        <div className="col-md-3"><select className="form-select form-select-sm" value={finishFilter} onChange={e => setFinishFilter(e.target.value)}><option value="">All Finishes</option>{filterOptions.finishes.map(f => <option key={f} value={f}>{f}</option>)}</select></div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label>Material (from Inventory)</label>
                                    <select className="form-select" value={selectedMaterial?.material_id || ""} onChange={(e) => { const mid = parseInt(e.target.value, 10); setSelectedMaterial(materialList.find(m => m.material_id === mid) || null); }}>
                                        <option value="">-- Select Material --</option>
                                        {filteredMaterials.map(m => {
                                            const onHand = m.stock_quantity, inbound = m.inbound_quantity, reserved = m.reserved_quantity || 0, totalAvail = onHand + inbound - reserved;
                                            return (<option key={m.material_id} value={m.material_id}>{m.material_code} - {m.description} [On-Hand: {onHand}, Inbound: {inbound}, Total: {totalAvail}]</option>);
                                        })}
                                    </select>
                                </div>
                            </>
                        )}

                        <hr />

                        {/* Sheets & Laser Time */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label>Sheets To Cut</label>
                                <input type="number" className="form-control" min={1} value={sheetsNeeded} onChange={(e) => { const val = parseInt(e.target.value, 10); setSheetsNeeded(val > 0 ? val : 1); }} />
                            </div>
                            <div className="col-md-6">
                                <label>Estimated Laser Time (minutes per sheet)</label>
                                <input type="number" className="form-control" value={laserMinutes} onChange={(e) => setLaserMinutes(e.target.value)} placeholder="e.g., 15" />
                            </div>
                        </div>

                        <hr />

                        {/* Parts Builder */}
                        <label className="form-label fw-bold">Add Parts to Sheet</label>
                        <div className="p-2 border rounded mb-3">
                            <div className="row g-2 align-items-end">
                                <div className="col-md-3">
                                    <label className="form-label-sm">1. Customer</label>
                                    <select className="form-select form-select-sm" value={partAddCustomerId || ""} onChange={e => { setPartAddCustomerId(e.target.value); setPartAddPartId(null); setPartAddSearch(""); }}>
                                        <option value="">-- Customer --</option>
                                        {customerList.map(c => (<option key={c.customer_id} value={c.customer_id}>{c.name}</option>))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label-sm">2. Part (type to search)</label>
                                    <input type="text" list="customModalPartList" className="form-control form-control-sm" placeholder="-- Type part ID --" value={partAddSearch} disabled={!partAddCustomerId}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setPartAddSearch(val);
                                            const match = filteredPartsForCustomer.find(p => `${p.product_id} - ${p.description}` === val || p.product_id === val);
                                            setPartAddPartId(match ? String(match.part_id) : null);
                                        }}
                                    />
                                    <datalist id="customModalPartList">
                                        {filteredPartsForCustomer.map(p => (<option key={p.part_id} value={p.product_id}>{p.product_id} - {p.description}</option>))}
                                    </datalist>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label-sm">3. Qty/Sheet</label>
                                    <input type="number" className="form-control form-control-sm" min={1} value={partAddQty} onChange={e => setPartAddQty(Math.max(1, parseInt(e.target.value) || 1))} />
                                </div>
                                <div className="col-md-3">
                                    <button className="btn btn-sm btn-secondary w-100" disabled={!partAddPartId} onClick={handleAddPartToSheet}>+ Add Part</button>
                                </div>
                            </div>
                        </div>

                        {/* Parts List */}
                        <strong>Parts on this Cut Sheet:</strong>
                        {chosenParts.length === 0 ? (
                            <p className="text-muted small mt-1">No parts added yet.</p>
                        ) : (
                            <ul className="list-group mt-2">
                                {chosenParts.map((cp, idx) => (
                                    <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div><strong>{cp.customer.name}</strong> - {cp.part.product_id}<br /><small className="text-muted">{cp.part.description}</small></div>
                                        <div>
                                            <span className="badge bg-primary rounded-pill me-3">Qty/Sheet: {cp.quantity}</span>
                                            <button type="button" className="btn-close" aria-label="Remove" onClick={() => setChosenParts(prev => prev.filter((_, i) => i !== idx))}></button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Preview */}
                        {chosenParts.length > 0 && (
                            <div className="mt-3">
                                <p className="mb-1"><strong>Sheets:</strong> {sheetsNeeded} &nbsp;|&nbsp; <strong>Est. Laser Time:</strong> {formatHoursAndMinutes(estimatedLaserHrs)}</p>
                                <table className="table table-sm table-bordered">
                                    <thead><tr><th>Customer</th><th>Part</th><th style={{width:'90px'}}>Qty/Sheet</th><th>Total Qty</th></tr></thead>
                                    <tbody>{previewParts.map((cp, idx) => (<tr key={idx}><td>{cp.customer.name}</td><td>{cp.part.product_id}</td><td><input type="number" className="form-control form-control-sm text-center" min={1} value={cp.quantity} onChange={e => { const newQty = Math.max(1, parseInt(e.target.value) || 1); setChosenParts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: newQty } : p)); }} style={{width:'70px'}} /></td><td>{cp.totalQuantity}</td></tr>))}</tbody>
                                </table>
                            </div>
                        )}

                        {/* Image Upload */}
                        <div className="mt-3">
                            <label>Cut Sheet Image (Optional)</label>
                            <input type="file" accept="image/*" className="form-control form-control-sm" onChange={(e) => { if (e.target.files?.[0]) checkAndSetFile(e.target.files[0]); }} />
                            {cutSheetImage && <img src={URL.createObjectURL(cutSheetImage)} alt="Preview" style={{ maxWidth: "300px", border: "1px solid #ccc", marginTop: "0.5rem" }} />}
                            <small className="text-muted d-block">You can also paste an image (Ctrl+V).</small>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                        <button className="btn btn-warning" onClick={handleSubmit} disabled={submitting || chosenParts.length === 0}>
                            {submitting ? <><span className="spinner-border spinner-border-sm me-1"></span>Creating...</> : <><i className="bi bi-plus-lg me-1"></i>Add Custom Cut Sheet to Grid</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =================================================================================================
// COMPONENT: Sales Order Dashboard (FIXED HOOK ORDER)
// =================================================================================================
function SalesOrderDashboard({ soDetails, bulkRows, productionPartList, onAddPartToBatch, jobCategory, pendingSurplusAllocations, onAllocateSurplus }) {
    // 1. Hook: Staged Quantities
    const stagedQuantities = useMemo(() => {
        const map = {};
        if (bulkRows) {
            bulkRows.forEach(row => {
                if (row.previewParts && row.previewParts.length > 0) {
                    row.previewParts.forEach(p => {
                        const pid = p.part_id; 
                        const qty = parseInt(p.totalQuantity, 10) || 0;
                        if (pid && qty > 0) {
                            map[pid] = (map[pid] || 0) + qty;
                        }
                    });
                }
                else if (row.part && row.quantity) {
                    const qty = parseInt(row.quantity, 10) || 0;
                    map[row.part.part_id] = (map[row.part.part_id] || 0) + qty;
                }
            });
        }
        return map;
    }, [bulkRows]);

    // 2. Hook: Flatten Lines (MUST BE BEFORE THE EARLY RETURN)
    const flattenedLines = useMemo(() => {
        if (!soDetails || !soDetails.lines) return [];
        return soDetails.lines.flatMap(item => {
            if (item.type === 'item') {
                return [item.data];
            } else if (item.type === 'assembly_group') {
                return item.components; 
            }
            return [];
        });
    }, [soDetails]);

    // 3. Early Return (Now safe because hooks are declared above)
    if (!soDetails || !soDetails.lines) return null;

    // 4. Filtering & Mapping
    const visibleLines = flattenedLines.filter(line => line.product_id && line.part_id);

    // A single part can appear on more than one SO line — e.g. a component shared
    // by two assemblies on the same order (SO-1066: FOOT/GUSSET/LEG/SKIRT live on
    // both 1TONTABLEA36 and 1TONTABLE304L), or two plain lines of the same part.
    // qty_in_production and qty_shipped are per-(SO, part) pools; since Aug 2026
    // /truth splits each pool across the part's lines in so_line_id order (the
    // shares sum to the pool; it used to stamp the whole pool on every line).
    // stagedQuantities is keyed by part_id. So collapse each part to a SINGLE row:
    // ordered and the pool shares are summed across its lines (partSurplus), the
    // staged pool is counted once.
    const partGroups = new Map(); // part_id -> { base: firstLine, members: [] }
    visibleLines.forEach(line => {
        if (!partGroups.has(line.part_id)) partGroups.set(line.part_id, { base: line, members: [] });
        partGroups.get(line.part_id).members.push(line);
    });

    // Pieces other orders send to each line (their surplus allocations into this order). They are
    // in qty_in_production but are NOT this order's surplus: POST /surplus_allocations counts only
    // this order's own jobs, so offering them made Create Jobs fail "Over-allocation: SO jobs
    // produced 562 ... surplus=-62" (SO-1139 FAB311162, 2026-10-21). features/salesOrders/surplus.js
    // (shared with the Sales Order page) splits own surplus from that over-coverage.
    const incoming = incomingByLine(flattenedLines);

    const lines = Array.from(partGroups.values()).map(({ base, members }) => {
        const staged = stagedQuantities[base.part_id] || 0;

        // Surplus allocations added in this session, not saved yet (posted after Create Jobs).
        // Saved ones (outgoing_allocations) are already in committedOut: without them a part whose
        // surplus was fully allocated still showed "+N surplus" + Allocate, and re-allocating it
        // made the backend reject the WHOLE batch ("already allocated=N, requested=N").
        const pendingAllocated = (pendingSurplusAllocations || [])
            .filter(a => a.part_id === base.part_id)
            .reduce((sum, a) => sum + (Number(a.quantity) || 0), 0);

        const s = partSurplus(members, { staged, pending: pendingAllocated, incoming });
        const { ordered, shipped, inProduction } = s;
        const remaining = Math.max(0, ordered - shipped - inProduction - staged);

        let statusClass = "";
        if (remaining === 0) statusClass = "table-success";
        else if (staged > 0) statusClass = "table-warning";

        // mergedLineCount > 1 → this row stands in for several SO lines (shown as
        // a small "×N lines" note so the operator knows it's a combined part).
        return {
            ...base, mergedLineCount: members.length, ordered, inProduction, shipped, staged, remaining,
            surplus: s.surplus,
            existingOpenAllocated: s.committedOut,
            pendingAllocated,
            allocatedSurplus: s.committedOut + pendingAllocated,
            unallocatedSurplus: s.allocatable,
            overCoverage: overCoverageText(s, s.sources),
            statusClass,
        };
    });

    const allFulfilled = lines.every(l => l.remaining === 0);

    const handleAddClick = (line) => {
        if (!productionPartList) return;
        const partObj = productionPartList.find(p => p.part_id === line.part_id);
        if (partObj) {
            onAddPartToBatch(partObj, line.remaining);
        } else {
            alert("Part definition not found in loaded parts list.");
        }
    };

    return (
        <div className="card mb-4 shadow-sm border-primary">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <i className="bi bi-cart-check me-2"></i> 
                    Sales Order Fulfillment: {soDetails.header.order_number}
                </h5>
                <span className="badge bg-white text-primary">
                    {allFulfilled ? "Order Fully Staged" : "Items Remaining"}
                </span>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Product</th>
                                <th>Description</th>
                                <th className="text-center">Ordered</th>
                                <th className="text-center" title="Quantity already shipped on this SO">Shipped</th>
                                <th className="text-center" title="Jobs already created in system (net of shipped + defects)">In Prod.</th>
                                <th className="text-center" title="Rows added below">Staging Now</th>
                                <th className="text-center">Remaining</th>
                                <th className="text-center" style={{width: '120px'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map(line => (
                                <tr key={line.part_id} className={line.statusClass}>
                                    <td className="fw-bold">
                                        {line.product_id}
                                        {line.mergedLineCount > 1 && (
                                            <span className="badge bg-light text-muted border ms-1 fw-normal" title={`This part is on ${line.mergedLineCount} order lines — combined here. Ordered = sum across lines.`}>
                                                ×{line.mergedLineCount} lines
                                            </span>
                                        )}
                                    </td>
                                    <td>{line.description}</td>
                                    <td className="text-center">{line.ordered}</td>
                                    <td className="text-center text-success fw-bold">
                                        {line.shipped > 0 ? line.shipped : '-'}
                                    </td>
                                    <td className="text-center">{line.inProduction}</td>
                                    <td className="text-center fw-bold text-primary">
                                        {line.staged > 0 ? `+${line.staged}` : '-'}
                                    </td>
                                    <td className="text-center fw-bold text-danger">
                                        {line.remaining > 0 ? line.remaining : <span className="text-success">✔</span>}
                                        {line.unallocatedSurplus > 0 ? (
                                            <span className="badge bg-warning text-dark ms-1" title={`${line.unallocatedSurplus} of ${line.surplus} surplus free to allocate — ${line.allocatedSurplus} already allocated`}>
                                                +{line.unallocatedSurplus} surplus
                                            </span>
                                        ) : line.surplus > 0 ? (
                                            <span className="badge bg-secondary ms-1" title={`${line.surplus} surplus, all ${line.allocatedSurplus} allocated to other SOs`}>
                                                +{line.surplus} allocated
                                            </span>
                                        ) : null}
                                        {line.overCoverage && (
                                            <span className="badge bg-info text-dark ms-1" title={line.overCoverage.title}>
                                                {line.overCoverage.label} ({line.overCoverage.detail})
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex gap-1 justify-content-center">
                                            {line.remaining > 0 && (
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleAddClick(line)}
                                                    title="Add remaining qty to batch"
                                                >
                                                    <i className="bi bi-plus-lg"></i> Add
                                                </button>
                                            )}
                                            {line.unallocatedSurplus > 0 && onAllocateSurplus && (
                                                <button
                                                    className="btn btn-sm btn-outline-warning"
                                                    onClick={() => onAllocateSurplus(line)}
                                                    title={`Allocate ${line.unallocatedSurplus} surplus parts to another SO`}
                                                >
                                                    <i className="bi bi-arrow-right-circle"></i> Allocate
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="card-footer text-muted small">
                * <strong>Shipped:</strong> Already out the door. <strong>In Prod:</strong> Quantity on existing active jobs (excluding shipped). <strong>Staging Now:</strong> Quantity in the creation table below. <strong>Remaining:</strong> Ordered − Shipped − In Prod − Staging.
            </div>
        </div>
    );
}

// =================================================================================================
// COMPONENT: OtherPartsBeingProducedTable
// Surfaces parts being produced (either in-staging via bulkRows, or on existing jobs linked
// to the current SO) that are NOT on any line of the current SO. These come from
// mixed-customer cut sheets and need to be allocated to another Sales Order.
// =================================================================================================
function OtherPartsBeingProducedTable({
    soDetails,
    bulkRows,
    productionPartList,
    customerList,
    pendingSurplusAllocations,
    onAllocateOrphan
}) {
    // Build set of part_ids already on the current SO lines (parts handled by the main
    // Sales Order Fulfillment table don't need to appear here).
    const soPartIds = useMemo(() => {
        const ids = new Set();
        if (!soDetails?.lines) return ids;
        soDetails.lines.forEach(item => {
            if (item.type === 'item' && item.data?.part_id) {
                ids.add(item.data.part_id);
            } else if (item.type === 'assembly_group') {
                (item.components || []).forEach(c => c.part_id && ids.add(c.part_id));
            }
        });
        return ids;
    }, [soDetails]);

    // Aggregate staging-now qty per orphan part from bulkRows.previewParts.
    const stagingByPart = useMemo(() => {
        const map = new Map();
        bulkRows.forEach(row => {
            (row.previewParts || []).forEach(pp => {
                if (!pp.part_id || soPartIds.has(pp.part_id)) return;
                const existing = map.get(pp.part_id) || {
                    part_id: pp.part_id,
                    product_id: pp.product_id,
                    description: pp.description,
                    staging_qty: 0
                };
                existing.staging_qty += pp.totalQuantity || 0;
                map.set(pp.part_id, existing);
            });
        });
        return map;
    }, [bulkRows, soPartIds]);

    // Merge with orphan_productions from the SO truth endpoint (previously-created jobs).
    const orphanFromTruth = soDetails?.orphan_productions || [];
    const allOrphanPartIds = Array.from(new Set([
        ...stagingByPart.keys(),
        ...orphanFromTruth.map(o => o.part_id)
    ]));

    const rows = allOrphanPartIds.map(partId => {
        const staging = stagingByPart.get(partId);
        const existing = orphanFromTruth.find(o => o.part_id === partId);
        const part = productionPartList?.find(p => p.part_id === partId);
        const customerId = part?.customer_id || existing?.part_customer_id;
        const customerName = (customerList?.find(c => c.customer_id === customerId)?.name)
            || existing?.part_customer_name
            || '(unknown)';

        // Sum existing (DB) + pending (in-session) orphan allocations for this part.
        const existingAllocated = Number(existing?.total_allocated || 0);
        const pendingAllocated = (pendingSurplusAllocations || [])
            .filter(a => a.part_id === partId && (a.source_so_line_id == null))
            .reduce((s, a) => s + (Number(a.quantity) || 0), 0);
        const totalAllocated = existingAllocated + pendingAllocated;

        const stagingQty = Number(staging?.staging_qty || 0);
        const existingQty = Number(existing?.total_quantity || 0);
        const totalProduced = stagingQty + existingQty;
        // Bad Idea Co. parts have no order to go to: they auto-ship to Bad Idea stock (utils/badIdeaParts)
        const badIdea = isBadIdeaCustomer(customerId);
        const unallocated = badIdea ? 0 : Math.max(0, totalProduced - totalAllocated);

        return {
            part_id: partId,
            product_id: staging?.product_id || existing?.product_id || part?.product_id || '(unknown)',
            description: staging?.description || existing?.description || part?.description || '',
            customer_id: customerId,
            customer_name: customerName,
            staging_qty: stagingQty,
            existing_qty: existingQty,
            totalProduced,
            totalAllocated,
            unallocated,
            badIdea,
            outgoingAllocations: existing?.outgoing_allocations || []
        };
    }).sort((a, b) => (a.product_id || '').localeCompare(b.product_id || ''));

    if (rows.length === 0) return null;

    const orderNumber = soDetails?.header?.order_number || 'this SO';
    const needsAllocation = rows.some(r => !r.badIdea);
    const hasBadIdea = rows.some(r => r.badIdea);

    return (
        <div className={`card mb-4 shadow-sm ${needsAllocation ? 'border-warning' : 'border-info'}`}
             data-testid="other-parts-being-produced">
            <div className={`card-header ${needsAllocation ? 'bg-warning-subtle' : 'bg-info-subtle'} d-flex justify-content-between align-items-center`}>
                <h5 className="mb-0">
                    <i className={`bi ${needsAllocation ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-2`}></i>
                    Other Parts Being Produced (cross-SO)
                </h5>
                <small className="text-muted">
                    These parts aren't on {orderNumber}.
                    {needsAllocation ? ' Allocate them to the correct Sales Order.' : ''}
                    {hasBadIdea ? ' Bad Idea Co. parts go to Bad Idea stock on their own.' : ''}
                </small>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Product</th>
                                <th>Description</th>
                                <th>Owning Customer</th>
                                <th className="text-center">Staging Now</th>
                                <th className="text-center" title="Quantity on existing active jobs linked to this SO">In Prod.</th>
                                <th className="text-center">Allocated</th>
                                <th className="text-center">Unallocated</th>
                                <th className="text-center" style={{ width: '140px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.part_id} data-testid={`other-part-${r.part_id}`}
                                    className={r.badIdea ? 'table-info' : (r.unallocated === 0 ? 'table-success' : 'table-warning')}>
                                    <td className="fw-bold">{r.product_id}</td>
                                    <td>{r.description}</td>
                                    <td>{r.customer_name}</td>
                                    <td className="text-center fw-bold text-primary">
                                        {r.staging_qty > 0 ? `+${r.staging_qty}` : '-'}
                                    </td>
                                    <td className="text-center">
                                        {r.existing_qty > 0 ? r.existing_qty : '-'}
                                    </td>
                                    <td className="text-center">
                                        {r.totalAllocated}
                                        {r.outgoingAllocations.length > 0 && (
                                            <span
                                                className="text-muted ms-1"
                                                title={r.outgoingAllocations
                                                    .map(a => `${a.target_so_order_number}: ${a.quantity}`)
                                                    .join(', ')}
                                            >
                                                <i className="bi bi-info-circle"></i>
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-center fw-bold text-danger">
                                        {r.badIdea ? (
                                            <span className="badge text-bg-info"
                                                  title="Bad Idea Co. has no sales orders: its pieces auto-ship to Bad Idea stock and are billed on its weekly invoice">
                                                Bad Idea stock
                                            </span>
                                        ) : (r.unallocated > 0 ? r.unallocated : <span className="text-success">✔</span>)}
                                    </td>
                                    <td className="text-center">
                                        {r.badIdea && (
                                            <span className="small text-muted">Auto-ships to Bad Idea</span>
                                        )}
                                        {!r.badIdea && r.unallocated > 0 && (
                                            <button
                                                className="btn btn-sm btn-outline-warning"
                                                onClick={() => onAllocateOrphan({
                                                    part_id: r.part_id,
                                                    part_product_id: r.product_id,
                                                    part_description: r.description,
                                                    part_customer_id: r.customer_id,
                                                    part_customer_name: r.customer_name,
                                                    unallocated: r.unallocated,
                                                    source_so_line_id: null
                                                })}
                                                title={`Allocate ${r.unallocated} to another Sales Order`}
                                            >
                                                <i className="bi bi-arrow-right-circle"></i> Allocate
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="card-footer text-muted small">
                * These parts come from shared cut sheets and belong to other Sales Orders.
                Allocate them so they can be properly shipped and invoiced.
                {hasBadIdea && (
                    <> Bad Idea Co. parts need no allocation: they auto-ship to Bad Idea stock and its
                    weekly invoice bills them.</>
                )}
            </div>
        </div>
    );
}

// =================================================================================================
// MAIN COMPONENT: JobEntryPage
// =================================================================================================
export default function JobEntryPage() {
  const location = useLocation();
  const customerPO = location.state?.customerPO;

  // ===================== Toggle Modes =====================
  // Options: "single", "bulk", "sales_order"
  const [entryMode, setEntryMode] = useState("single"); 
  const [jobCategory, setJobCategory] = useState("Sheet Stock"); 

  // ===================== Shared State =====================
  const [customerList, setCustomerList] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const [cutSheetList, setCutSheetList] = useState([]);
  const [productionPartList, setProductionPartList] = useState([]);
  const [assemblyList, setAssemblyList] = useState([]);
  const [cutListList, setCutListList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  
  // NEW: Cut Sheet Sets
  const [cutSheetSets, setCutSheetSets] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // ===================== PO Upload State =====================
  const [poFile, setPoFile] = useState(null);

  // ===================== Sales Order Mode State =====================
  const [salesOrderList, setSalesOrderList] = useState([]);
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState("");
  const [soDetails, setSoDetails] = useState(null); 
  
  // NEW: Fulfillment Method Toggle (By Part vs By Set)
  const [fulfillmentMethod, setFulfillmentMethod] = useState("part"); // 'part' or 'set'

  // ===================== Single-Job Entry State =====================
  const [jobType, setJobType] = useState("Production"); 
  const [customerId, setCustomerId] = useState(null);
  const [poNumber, setPoNumber] = useState("");
  const [soNumber, setSoNumber] = useState("");
  const [shippingMethod, setShippingMethod] = useState("Customer Pickup"); 
  const [jobDescription, setJobDescription] = useState("");
  const [priority, setPriority] = useState(3);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [maintenanceWorkstation, setMaintenanceWorkstation] = useState("Laser");

  // Assembly Specific State
  const [targetAssembly, setTargetAssembly] = useState(null);
  const [buildQty, setBuildQty] = useState(1);
  const [recipeInfo, setRecipeInfo] = useState(null);
  const [yieldAlert, setYieldAlert] = useState(null);

  // Single-job Production fields
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedCutSheet, setSelectedCutSheet] = useState(null);
  const [requestedQuantity, setRequestedQuantity] = useState("");
  const [tempRequestedQuantity, setTempRequestedQuantity] = useState("");
  const [productionPreviewParts, setProductionPreviewParts] = useState([]);
  const [productionSheetsNeeded, setProductionSheetsNeeded] = useState(0);
  const [productionEstimatedLaserHrs, setProductionEstimatedLaserHrs] = useState(0);

  // Single-job Custom fields
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [customChosenParts, setCustomChosenParts] = useState([]);
  const [customSheetsNeeded, setCustomSheetsNeeded] = useState(1);
  const [customEstimatedLaserHrs, setCustomEstimatedLaserHrs] = useState(0);
  const [customPreviewParts, setCustomPreviewParts] = useState([]);
  const [customCutSheetImage, setCustomCutSheetImage] = useState(null);
  const [customLaserMinutes, setCustomLaserMinutes] = useState("");
  const [useCustomMaterial, setUseCustomMaterial] = useState(false);
  const [customMaterialDesc, setCustomMaterialDesc] = useState("");

  const [selectedCutList, setSelectedCutList] = useState(null);
  const [shapeRequestedQuantity, setShapeRequestedQuantity] = useState("");
  const [tempShapeRequestedQuantity, setTempShapeRequestedQuantity] = useState("");
  const [sticksNeeded, setSticksNeeded] = useState(0);
  const [shapeStockPreviewParts, setShapeStockPreviewParts] = useState([]);

  // ===================== Bulk Mode State =====================
  const [bulkCustomerId, setBulkCustomerId] = useState(null);
  const [bulkPoNumber, setBulkPoNumber] = useState("");
  const [bulkSoNumber, setBulkSoNumber] = useState("");
  const [bulkShippingMethod, setBulkShippingMethod] = useState("Customer Pickup"); 
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkCreatedJobs, setBulkCreatedJobs] = useState([]);
  // T&M (Commit C): what add_cut_sheet / add_cut_list said about jobs loaded onto a T&M order.
  const [tmLoadHints, setTmLoadHints] = useState(null);
  
  const [assemblyJobId, setAssemblyJobId] = useState(null);
  // Replaced individual added lists with unified tracker in the new component

  // ===================== Surplus Allocation State =====================
  const [pendingSurplusAllocations, setPendingSurplusAllocations] = useState([]);
  const [showSurplusModal, setShowSurplusModal] = useState(false);
  const [surplusModalContext, setSurplusModalContext] = useState(null); // { part_id, part_product_id, surplus, source_so_line_id }

  // ===================== Custom Cut Sheet Modal State =====================
  const [showCustomCutSheetModal, setShowCustomCutSheetModal] = useState(false);

// ===================== Load Basic Data On Mount =====================
  useEffect(() => {
    loadCustomers();
    loadMaterials();
    loadCutSheets();
    loadParts();
    loadCutLists();
    loadAssemblies();
    loadCutSheetSets(); // New
    loadUsers();
  }, []);

  useEffect(() => {
    if (customerPO) {
      setCustomerId(customerPO.customer_id);
      setBulkCustomerId(customerPO.customer_id);
      setJobDescription(customerPO.original_filename || `Job from PO #${customerPO.document_id}`);
    }
  }, [customerPO]);

  // The PO file is private in S3; the API hands out a signed link that expires
  // after an hour, and router state can be older than that (a reload keeps it),
  // so ask for a fresh one whenever the viewer opens.
  const [customerPODocUrl, setCustomerPODocUrl] = useState(null);
  const customerPOId = customerPO?.document_id;
  useEffect(() => {
    if (!customerPOId) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/customer_pos/${customerPOId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && d.s3_url) setCustomerPODocUrl(d.s3_url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customerPOId]);

  // Reset specific fields when toggling category
  useEffect(() => {
    setSelectedPart(null);
    setSelectedCutSheet(null);
    setSelectedCutList(null);
    setRequestedQuantity("");
    setTempRequestedQuantity("");
    setShapeRequestedQuantity("");
    setTempShapeRequestedQuantity("");
  }, [jobCategory]);

  // Sales Order List Fetching
  useEffect(() => {
    // If in sales_order mode and we have a customer ID, fetch their open orders
    if (entryMode === "sales_order" && bulkCustomerId) {
        fetch(`${API_BASE}/sales_orders?customer_id=${bulkCustomerId}&status=Open`)
            .then(res => res.json())
            .then(data => setSalesOrderList(data))
            .catch(err => console.error("Failed to load sales orders", err));
    } else {
        setSalesOrderList([]);
    }
  }, [entryMode, bulkCustomerId]);

  // Sales Order Details Fetching
  useEffect(() => {
      if (selectedSalesOrderId) {
          setLoading(true);
          fetch(`${API_BASE}/sales_orders/${selectedSalesOrderId}/truth`)
            .then(res => res.json())
            .then(data => {
                setSoDetails(data);
                // Auto-fill PO info if present
                if(data.header.customer_po) setBulkPoNumber(data.header.customer_po);
                if(data.header.customer_so) setBulkSoNumber(data.header.customer_so);
                // Apply the SO's scheduling date to any row that hasn't been
                // manually edited yet (internal target overrides the promised
                // date when set). Flagged via row.dueDateDirty on change.
                const promised = data.header?.target_completion_date || data.header?.promised_date;
                if (promised) {
                    const promisedIso = promised.slice(0, 10);
                    setBulkRows(prev => prev.map(r =>
                        r.dueDateDirty ? r : { ...r, dueDate: promisedIso }
                    ));
                }
            })
            .catch(err => {
                console.error("Failed to fetch SO truth", err);
                setError("Failed to load Sales Order details.");
            })
            .finally(() => setLoading(false));
      } else {
          setSoDetails(null);
      }
  }, [selectedSalesOrderId]);

  // Recipe Logic
  useEffect(() => {
      if (targetAssembly) {
          fetch(`${API_BASE}/assemblies/${targetAssembly.assembly_id}/yield_info`)
            .then(res => res.json())
            .then(data => {
                setRecipeInfo(data);
                if(data.has_recipe && data.batch_size > 1) {
                    setBuildQty(data.batch_size);
                } else {
                    setBuildQty(1);
                }
            })
            .catch(err => console.error("Failed to load yield info:", err));
      } else {
          setRecipeInfo(null);
          setYieldAlert(null);
      }
  }, [targetAssembly]);

  const handleAssemblyQtyBlur = () => {
      if (recipeInfo && recipeInfo.has_recipe && recipeInfo.batch_size > 1) {
          const bSize = recipeInfo.batch_size;
          const remainder = buildQty % bSize;
          
          if (remainder !== 0) {
              const batchesNeeded = Math.ceil(buildQty / bSize);
              const newQty = batchesNeeded * bSize;
              setBuildQty(newQty);
              setYieldAlert(`Quantity auto-adjusted to ${newQty} to match standard production batch size of ${bSize}. (${batchesNeeded} full runs)`);
              setTimeout(() => setYieldAlert(null), 5000);
          } else {
              setYieldAlert(null);
          }
      }
  };


  async function loadCustomers() {
    try {
      const res = await fetch(`${API_BASE}/customers`);
      if (res.ok) setCustomerList((await res.json()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) { console.error(err); }
  }

  async function loadMaterials() {
    try {
      const res = await fetch(`${API_BASE}/materials`);
      if (res.ok) setMaterialList(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadCutSheets() {
    try {
      const res = await fetch(`${API_BASE}/cut_sheets?active_only=true`);
      if (res.ok) setCutSheetList(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadParts() {
    try {
      const res = await fetch(`${API_BASE}/parts`);
      if (res.ok) setProductionPartList(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadCutLists() {
    try {
      const res = await fetch(`${API_BASE}/cut_lists?active_only=true`);
      if (res.ok) setCutListList(await res.json());
    } catch (err) { console.error(err); }
  }

  // NEW: Fetch Assemblies
  async function loadAssemblies() {
    try {
      const res = await fetch(`${API_BASE}/assemblies`);
      if (res.ok) setAssemblyList(await res.json());
    } catch (err) { console.error(err); }
  }
  
  // NEW: Fetch Cut Sheet Sets (active only for job entry)
  async function loadCutSheetSets() {
    try {
      const res = await fetch(`${API_BASE}/cut_sheet_sets?active_only=true`);
      if (res.ok) setCutSheetSets(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadUsers() {
    try {
      const res = await fetch(`${API_BASE}/users`);
      if (res.ok) {
        const data = await res.json();
        setUsersList(data.filter(u => u.is_active));
      }
    } catch (err) { console.error(err); }
  }

  function getDefaultDueDate() {
    // Internal target completion date overrides the promised date for
    // scheduling; jobs created for the SO should follow the same date.
    const schedDate = soDetails?.header?.target_completion_date || soDetails?.header?.promised_date;
    if (entryMode === "sales_order" && schedDate) {
      return schedDate.slice(0, 10);
    }
    return shopToday(new Date(Date.now() + 7 * 86400000)); // a week out, the shop's (Central) day
  }

  const filteredProductionPartsSingle = useMemo(() => {
    let filtered = productionPartList.filter(p => p.stock_type === jobCategory);
    if (jobType === "Production" && customerId) {
      filtered = filtered.filter((p) => p.customer_id === parseInt(customerId, 10));
    }
    return filtered;
  }, [jobType, customerId, productionPartList, jobCategory]);

  const filteredProductionPartsBulk = useMemo(() => {
    let filtered = productionPartList.filter(p => p.stock_type === jobCategory);
    if (bulkCustomerId) {
      filtered = filtered.filter((p) => p.customer_id === parseInt(bulkCustomerId, 10));
    }
    return filtered;
  }, [bulkCustomerId, productionPartList, jobCategory]);

  // Production Preview Logic
  useEffect(() => {
    if (entryMode !== "single" || jobType !== "Production") return;
    if (!selectedPart || !selectedCutSheet || !requestedQuantity) {
      setProductionPreviewParts([]); setProductionSheetsNeeded(0); setProductionEstimatedLaserHrs(0);
      return;
    }
    const rawQty = parseInt(requestedQuantity, 10);
    if (isNaN(rawQty) || rawQty <= 0) {
      setProductionPreviewParts([]); setProductionSheetsNeeded(0); setProductionEstimatedLaserHrs(0);
      return;
    }
    let primaryQPS = 1;
    if (selectedCutSheet.parts?.find) {
      const match = selectedCutSheet.parts.find((p) => p.part_id === selectedPart.part_id);
      if (match) primaryQPS = match.quantity_per_sheet;
    }
    const sheetsNeeded = rawQty / primaryQPS;
    const previewRows = selectedCutSheet.parts?.map(p => ({ ...p, totalQuantity: p.quantity_per_sheet * sheetsNeeded })) || [];
    const avgHrs = selectedCutSheet.avg_hrs_per_sheet || 1.0;
    const estLaserHrs = sheetsNeeded * avgHrs;
    setProductionPreviewParts(previewRows);
    setProductionSheetsNeeded(sheetsNeeded);
    setProductionEstimatedLaserHrs(estLaserHrs);
  }, [entryMode, jobType, selectedPart, selectedCutSheet, requestedQuantity]);

  // Custom Job Preview Logic
  useEffect(() => {
    if (entryMode !== "single" || jobType !== "Custom") return;
    
    const hasMaterialInfo = useCustomMaterial ? customMaterialDesc.trim() !== '' : !!selectedMaterial;
    if (!hasMaterialInfo || customChosenParts.length === 0) {
      setCustomEstimatedLaserHrs(0); 
      setCustomPreviewParts([]);
      return;
    }

    const previewList = customChosenParts.map(cp => ({ ...cp, totalQuantity: cp.quantity * customSheetsNeeded }));
    setCustomPreviewParts(previewList);

    let totalHrs = 0;
    if (customLaserMinutes && parseInt(customLaserMinutes, 10) > 0) {
      const minutesPerSheet = parseInt(customLaserMinutes, 10);
      totalHrs = (minutesPerSheet / 60) * customSheetsNeeded;
    } else {
      previewList.forEach(cp => {
        const hrsEach = cp.part.estimated_hrs_each ?? 0.1;
        totalHrs += hrsEach * cp.totalQuantity;
      });
    }
    setCustomEstimatedLaserHrs(totalHrs);
  }, [entryMode, jobType, selectedMaterial, customChosenParts, customSheetsNeeded, useCustomMaterial, customMaterialDesc, customLaserMinutes]);

  // Shape Stock Preview Logic
  useEffect(() => {
    if (entryMode !== "single" || jobCategory !== "Shape Stock" || !selectedPart || !selectedCutList || !shapeRequestedQuantity) {
      setShapeStockPreviewParts([]); setSticksNeeded(0);
      return;
    }
    const qty = parseInt(shapeRequestedQuantity, 10);
    if(isNaN(qty) || qty <= 0) {
      setShapeStockPreviewParts([]); setSticksNeeded(0);
      return;
    }
    let primaryQPL = 1;
    if (selectedCutList.parts) {
        const match = selectedCutList.parts.find(p => p.part_id === selectedPart.part_id);
        if (match && match.quantity_per_length > 0) primaryQPL = match.quantity_per_length;
    }
    const sticks = Math.ceil(qty / primaryQPL);
    setSticksNeeded(sticks);
    const previewRows = selectedCutList.parts?.map(p => ({ ...p, totalQuantity: p.quantity_per_length * sticks })) || [];
    setShapeStockPreviewParts(previewRows);
  }, [entryMode, jobCategory, selectedPart, selectedCutList, shapeRequestedQuantity]);

  // ===================== HELPER: Upload PO File =====================
  const uploadPOFile = async (targetCustomerId) => {
    if (!poFile) return null;
    const formData = new FormData();
    formData.append("file", poFile);
    formData.append("customer_id", targetCustomerId);
    
    try {
      const res = await fetch(`${API_BASE}/customer_pos/upload`, { method: "POST", body: formData });
      if (!res.ok) throw new Error(`PO Upload failed: ${await res.text()}`);
      const data = await res.json();
      return data.customer_po_id; 
    } catch (err) {
      console.error("Error uploading PO:", err);
      throw err;
    }
  };

  // ===================== HELPER: Link PO to Job =====================
  const linkPOToJob = async (jobId, documentId) => {
    if (!documentId) return;
    try {
      await fetch(`${API_BASE}/jobs/${jobId}/link_customer_po`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_po_id: documentId }) 
      });
    } catch (err) {
      console.error("Error linking PO:", err);
    }
  };

  // ===================== Single-Job: Create Handler =====================
  async function handleSingleJobCreate() {
    setLoading(true);
    setError(null);

    if (!jobDescription) { setError("Job Description is required."); setLoading(false); return; }
    if (!dueDate) { setError("An order due date is required."); setLoading(false); return; }
    if ((jobType === "Production" || jobType === "Custom" || jobType === "Assembly") && !customerId) {
      setError("Please select a customer."); setLoading(false); return;
    }

    // NEW: Validation for Assembly Job Type
    if (jobType === "Assembly" && !targetAssembly) {
        setError("Please select which Assembly you are building.");
        setLoading(false);
        return;
    }

    // Validation for Sheet Stock Production: part, cut sheet, and quantity are all required
    if (jobCategory === "Sheet Stock" && jobType === "Production") {
        if (!selectedPart) { setError("Please select a part."); setLoading(false); return; }
        if (!selectedCutSheet) { setError("Please select a cut sheet."); setLoading(false); return; }
        if (!requestedQuantity || parseInt(requestedQuantity, 10) <= 0) {
            setError("Requested quantity is required and must be greater than 0."); setLoading(false); return;
        }
    }

    // Validation for Shape Stock Production: part, cut list, and quantity are all required
    if (jobCategory === "Shape Stock" && jobType === "Production") {
        if (!selectedPart) { setError("Please select a part."); setLoading(false); return; }
        if (!selectedCutList) { setError("Please select a cut list."); setLoading(false); return; }
        if (!shapeRequestedQuantity || parseInt(shapeRequestedQuantity, 10) <= 0) {
            setError("Requested quantity is required and must be greater than 0."); setLoading(false); return;
        }
    }

    // Hoisted so the catch can roll back an empty header whose cut-source attach
    // failed (two-phase create orphan — the job-7338 bug).
    let createdJob = null;
    try {
      // 1. Upload PO if attached
      let newPoId = null;
      if (poFile && customerId) {
        newPoId = await uploadPOFile(customerId);
      }
      const finalPoId = newPoId || (customerPO ? customerPO.document_id : null);

      // 2. Create Job
      const payload = {
        job_type: jobType, 
        job_category: jobCategory, 
        customer_id: jobType === "Maintenance" ? null : customerId,
        job_description: jobDescription, 
        customer_po: poNumber || null,
        customer_so: soNumber || null,
        shipping_method: shippingMethod,
        priority_level: parseInt(priority, 10), 
        due_date: dueDate || null, 
        notes: notes || ""
      };

      const createRes = await fetch(`${API_BASE}/jobs`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
      });
      if (!createRes.ok) throw new Error(await apiErrorMessage(createRes));
      createdJob = await createRes.json();

      // 3. Link job to customer PO
      if (finalPoId) {
        await linkPOToJob(createdJob.job_id, finalPoId);
      }
      
      if (jobType === "Assembly") {
        setAssemblyJobId(createdJob.job_id); 
        // We set the description to show what assembly we are building if it wasn't typed manually
        if (!jobDescription) setJobDescription(createdJob.job_description);
        setLoading(false); 
        return; // Don't redirect yet, let user add components
      }

      if (jobCategory === "Sheet Stock" && jobType === "Production" && selectedPart && selectedCutSheet && requestedQuantity) {
        const body = { part_id: selectedPart.part_id, cut_sheet_id: selectedCutSheet.cut_sheet_id, requested_quantity: parseInt(requestedQuantity, 10) };
        const res = await fetch(`${API_BASE}/jobs/${createdJob.job_id}/add_cut_sheet`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
      } else if (jobCategory === "Sheet Stock" && jobType === "Custom") {
        await addCutSheetForCustom(createdJob.job_id);
      } else if (jobCategory === "Shape Stock" && jobType === "Production" && selectedPart && selectedCutList && shapeRequestedQuantity) {
        const body = { part_id: selectedPart.part_id, cut_list_id: selectedCutList.cut_list_id, requested_quantity: parseInt(shapeRequestedQuantity, 10) || 0 };
        const res = await fetch(`${API_BASE}/jobs/${createdJob.job_id}/add_cut_list`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
      } else if (jobType === "Maintenance") {
        await addMaintenanceTask(createdJob.job_id);
      }

      setLoading(false);
      alert(`Job ${createdJob.job_id} created successfully!`);
      navigate("/jobs");
    } catch (err) {
      // Two-phase create rollback: if the job header was created but its cut
      // source failed to attach, delete the empty orphan (job-7338 bug). Scoped
      // to the cut-sheet/cut-list paths — Assembly shells are intentionally empty
      // until components are added, and Maintenance jobs carry a task, not a part.
      const isCutSourcePath = (jobType === "Production" || jobType === "Custom")
        && (jobCategory === "Sheet Stock" || jobCategory === "Shape Stock");
      if (createdJob?.job_id && isCutSourcePath) {
        try {
          const partsRes = await fetch(`${API_BASE}/jobs/${createdJob.job_id}/parts`);
          const jp = partsRes.ok ? await partsRes.json() : [];
          if (jp.length === 0) {
            await fetch(`${API_BASE}/jobs/${createdJob.job_id}`, { method: "DELETE" });
          }
        } catch (e) { /* best effort cleanup */ }
      }
      setLoading(false);
      setError(err.message);
    }
  }

  async function addMaintenanceTask(jobId) {
      const body = { workstation: maintenanceWorkstation, status: "Pending" };
      const res = await fetch(`${API_BASE}/jobs/${jobId}/maintenance_task`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
  }
  
  async function addCutSheetForCustom(jobId) {
    if (customChosenParts.length === 0) throw new Error("No parts have been added to the custom sheet.");
    if (!useCustomMaterial && !selectedMaterial) throw new Error("Please select an inventoried material or toggle to describe a non-inventory one.");
    if (useCustomMaterial && !customMaterialDesc.trim()) throw new Error("Please describe the non-inventory material.");

    const csBody = {
      cut_sheet_name: `CustomCS_Job${jobId}_${Date.now()}`, description: "Temporary cut sheet for custom job",
      is_temporary: true, active_status: false, notes: "Auto-generated for custom job.",
      initial_est_laser_minutes: customLaserMinutes ? parseInt(customLaserMinutes, 10) : null,
      material_id: useCustomMaterial ? null : selectedMaterial.material_id,
      custom_material_description: useCustomMaterial ? customMaterialDesc : null,
    };
    
    const csRes = await fetch(`${API_BASE}/cut_sheets`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(csBody)
    });
    if (!csRes.ok) throw new Error(`Failed to create temporary cut sheet: ${await csRes.text()}`);
    const csData = await csRes.json();

    for (const cp of customChosenParts) {
      const cspBody = { part_id: cp.part.part_id, quantity_per_sheet: cp.quantity, customer_id: cp.customer.customer_id };
      const cspRes = await fetch(`${API_BASE}/cut_sheets/${csData.cut_sheet_id}/parts`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cspBody)
      });
      if (!cspRes.ok) throw new Error(`Failed to add part ${cp.part.product_id}: ${await cspRes.text()}`);
    }

    if (customCutSheetImage) {
      try {
        const formData = new FormData();
        formData.append("file", customCutSheetImage);
        const uploadRes = await fetch(`${API_BASE}/cut_sheets/${csData.cut_sheet_id}/upload-image`, { method: "POST", body: formData });
        if (!uploadRes.ok) console.warn("Cut sheet image upload failed:", await uploadRes.text());
      } catch (err) { console.warn("Image upload error:", err); }
    }

    const primary = customChosenParts[0];
    const finalRequested = primary.quantity * customSheetsNeeded;
    const addBody = { part_id: primary.part.part_id, cut_sheet_id: csData.cut_sheet_id, requested_quantity: finalRequested };
    const addRes = await fetch(`${API_BASE}/jobs/${jobId}/add_cut_sheet`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(addBody)
    });
    if (!addRes.ok) throw new Error(await apiErrorMessage(addRes, 'Failed to attach cut sheet to job'));
  }

  // ===================== Bulk Logic =====================
  function handleAddBulkRow(initialData = {}) {
    const newRow = {
        id: Date.now(),
        part: initialData.part || null,
        partSearch: initialData.part ? initialData.part.product_id : "",
        cutSheet: null,
        cutList: null,
        quantity: initialData.quantity || 0,
        tempQuantity: initialData.quantity ? String(initialData.quantity) : "",
        jobPriority: 2,
        dueDate: getDefaultDueDate(),
        authorizedUserId: "",
        notes: "",
        previewParts: [],
        sheetsNeeded: 0,
        sticksNeeded: 0,
        laserHrs: 0,
        error: null
    };
    
    // Trigger calculation updates if pre-filled
    if (initialData.part && initialData.quantity) {
        // We delay the set state slightly or just rely on the effect loop
        // Ideally, we want to simulate the 'blur' logic.
        // For simplicity, we just add it, and the useEffect loop below will detect 
        // the missing CutSheet/CutList and force the user to select one, OR
        // we can try to auto-select best cut sheet here.
    }
    
    setBulkRows(prev => [...prev, newRow]);
  }

  function handleDuplicateLastRow() {
    if (bulkRows.length === 0) return;
    const lastRow = bulkRows[bulkRows.length - 1];
    const newRow = { ...lastRow, id: Date.now(), error: null };
    setBulkRows(prev => [...prev, newRow]);
  }

  function handleRemoveBulkRow(rowId) {
    setBulkRows(prev => {
      const row = prev.find(r => r.id === rowId);
      // Clean up temporary cut sheet if this is a custom row and no other rows share it
      if (row?.customCutSheetId) {
        const otherRowsWithSameSheet = prev.filter(r => r.id !== rowId && r.customCutSheetId === row.customCutSheetId);
        if (otherRowsWithSameSheet.length === 0) {
          fetch(`${API_BASE}/cut_sheets/${row.customCutSheetId}`, { method: 'DELETE' }).catch(() => {});
        }
      }
      return prev.filter(r => r.id !== rowId);
    });
  }

  function handleBulkRowChange(rowId, field, value) {
    setBulkRows(prev => prev.map(r => (r.id === rowId ? { ...r, [field]: value } : r)));
  }

  // NEW: Handler passed to SalesOrderDashboard
  const handleAddPartFromSO = (partObj, qty) => {
      handleAddBulkRow({ part: partObj, quantity: qty });
  };

  // Handler for custom cut sheet modal submission
  const handleCustomCutSheetSubmit = (rowData) => {
      setBulkRows(prev => [...prev, rowData]);
      setShowCustomCutSheetModal(false);
  };

  // Helper to "unroll" a set into individual grid rows
  const handleStageSetToGrid = (setId, multiplier) => {
      // 1. Find all active sheets belonging to this set
      const sheetsInSet = cutSheetList.filter(cs => cs.cut_sheet_set_id === parseInt(setId) && cs.active_status !== false);
      
      if (sheetsInSet.length === 0) {
          alert("No active cut sheets found in this set.");
          return;
      }

      // 2. Map sheets to bulk rows
      const newRows = sheetsInSet.map(cs => {
          // Calculate exact number of sheets needed
          // (Sheets per Set definition) * (Number of Sets requested)
          const sheetsDefinedInSet = cs.quantity_in_set || 1;
          const totalSheetsNeeded = sheetsDefinedInSet * multiplier;

          // We need to anchor the row to a "Primary Part" for the grid to work.
          // We grab the first part on the sheet.
          const primaryPartRef = cs.parts && cs.parts.length > 0 ? cs.parts[0] : null;
          
          // Find full part details from master list
          const fullPart = primaryPartRef 
              ? productionPartList.find(p => p.part_id === primaryPartRef.part_id)
              : null;

          // Calculate the "Part Quantity" to drive the grid logic.
          // Grid Logic: (User Enters Part Qty) / (Parts Per Sheet) = Sheets Needed
          // Reverse Logic: (Sheets Needed) * (Parts Per Sheet) = Part Qty to Enter
          const partsPerSheet = primaryPartRef ? primaryPartRef.quantity_per_sheet : 1;
          const calculatedPartQty = totalSheetsNeeded * partsPerSheet;

          return {
              id: Date.now() + Math.random(), // Unique ID
              part: fullPart,
              partSearch: fullPart ? fullPart.product_id : "",
              cutSheet: cs,
              quantity: calculatedPartQty,
              tempQuantity: String(calculatedPartQty),
              jobPriority: 2,
              dueDate: getDefaultDueDate(),
              authorizedUserId: "",
              notes: "",
              // Initialize derived fields so the grid calculates correctly immediately
              previewParts: [],
              sheetsNeeded: 0,
              laserHrs: 0,
              error: fullPart ? null : "No parts found on this sheet to anchor job"
          };
      });

      // 3. Add to state and switch view
      setBulkRows(prev => [...prev, ...newRows]);
      setFulfillmentMethod('part'); // Switch UI back to the Grid view
  };

  useEffect(() => {
    if (entryMode !== "bulk" && entryMode !== "sales_order") return;
    
    setBulkRows(oldRows => {
      let changed = false;
      const newRows = oldRows.map(row => {
        const rowType = row.part?.stock_type || 'Sheet Stock';

        // -------------------------------------------------------
        // SHEET STOCK LOGIC
        // -------------------------------------------------------
        if (rowType === 'Sheet Stock') {
            // Custom rows are fully pre-calculated from the modal — skip recalc
            if (row.isCustom) return row;

            let rowError = row.error;

            // Validate Cut Sheet status (Archived/Obsolete)
            if (row.cutSheet) {
                if (row.cutSheet.is_current_revision === false) {
                    rowError = "Selected Cut Sheet is Archived. Please update.";
                } else if (row.cutSheet.has_obsolete_parts) {
                    rowError = "Cut Sheet contains obsolete parts. Update required.";
                } else if (rowError === "Selected Cut Sheet is Archived. Please update." ||
                           rowError === "Cut Sheet contains obsolete parts. Update required.") {
                    rowError = null;
                }
            }

            // If missing required fields, reset derived data but keep user input
            if (!row.part || !row.cutSheet || !row.quantity) {
                if (row.previewParts.length || row.sheetsNeeded !== 0 || row.laserHrs !== 0 || row.error !== rowError) changed = true;
                return { ...row, previewParts: [], sheetsNeeded: 0, laserHrs: 0, error: rowError };
            }

            // 1. Calculate Qty Per Sheet (QPS)
            const rawQty = parseInt(row.quantity, 10) || 0;
            let qps = 1;
            if (row.cutSheet?.parts?.find) {
                const match = row.cutSheet.parts.find(p => p.part_id === row.part.part_id);
                if (match) qps = match.quantity_per_sheet;
            }
            
            // 2. Calculate Sheets Needed
            const newSheets = qps > 0 ? Math.ceil(rawQty / qps) : 0;
            
            // 3. Calculate "Rolled" Quantity (Snap to full sheet yield)
            const rolledQty = newSheets * qps;

            // 4. Calculate Derived Data
            // FIX: Normalize 'part_description' to 'description' for the Summary Table
            const subParts = row.cutSheet.parts?.map(p => ({ 
                ...p, 
                description: p.part_description || p.description, 
                totalQuantity: p.quantity_per_sheet * newSheets 
            })) || [];
            
            const newLaser = newSheets * (row.cutSheet.avg_hrs_per_sheet || 1.0);

            // 5. Determine if an update is needed
            // We update if the calculated quantity differs from stored, OR if other stats changed.
            // NOTE: We only force rolledQty if a cut sheet is actually selected.
            const quantityToStore = row.cutSheet ? rolledQty : rawQty;
            
            const rowHasChanged = newSheets !== row.sheetsNeeded || 
                                  newLaser !== row.laserHrs || 
                                  JSON.stringify(subParts) !== JSON.stringify(row.previewParts) || 
                                  row.error !== rowError ||
                                  quantityToStore !== rawQty;

            if (rowHasChanged) {
                changed = true;
                return { 
                    ...row, 
                    sheetsNeeded: newSheets, 
                    laserHrs: newLaser, 
                    previewParts: subParts, 
                    sticksNeeded: 0, 
                    error: rowError,
                    quantity: quantityToStore,          
                    tempQuantity: String(quantityToStore) // Update UI input to show rolled number
                };
            }
        
        // -------------------------------------------------------
        // SHAPE STOCK LOGIC
        // -------------------------------------------------------
        } else if (rowType === 'Shape Stock') {
            
            if (!row.part || !row.cutList || !row.quantity) {
                if (row.previewParts.length || row.sticksNeeded !== 0) changed = true;
                return { ...row, previewParts: [], sticksNeeded: 0, sheetsNeeded: 0, laserHrs: 0 };
            }

            // 1. Calculate Qty Per Length (QPL)
            const rawQty = parseInt(row.quantity, 10) || 0;
            let qpl = 1;
            if (row.cutList?.parts?.find) {
                const match = row.cutList.parts.find(p => p.part_id === row.part.part_id);
                if (match) qpl = match.quantity_per_length;
            }

            // 2. Calculate Sticks Needed
            const newSticks = qpl > 0 ? Math.ceil(rawQty / qpl) : 0;
            
            // 3. Calculate "Rolled" Quantity (Snap to full stick yield)
            const rolledQty = newSticks * qpl;

            // 4. Calculate Derived Data
            // FIX: Normalize 'part_description' to 'description' here as well
            const subParts = row.cutList.parts?.map(p => ({ 
                ...p, 
                description: p.part_description || p.description,
                totalQuantity: Math.round(p.quantity_per_length * newSticks) 
            })) || [];

            // 5. Determine if an update is needed
            const quantityToStore = row.cutList ? rolledQty : rawQty;

            const rowHasChanged = newSticks !== row.sticksNeeded || 
                                  JSON.stringify(subParts) !== JSON.stringify(row.previewParts) ||
                                  quantityToStore !== rawQty;

            if (rowHasChanged) {
                changed = true;
                return { 
                    ...row, 
                    sticksNeeded: newSticks, 
                    previewParts: subParts, 
                    sheetsNeeded: 0, 
                    laserHrs: 0,
                    quantity: quantityToStore,
                    tempQuantity: String(quantityToStore) // Update UI input to show rolled number
                };
            }
        }
        return row;
      });
      return changed ? newRows : oldRows;
    });
  }, [entryMode, bulkRows, jobCategory]);

  // B-1 Layer B: fetch expected_job_qty for assembly-component parts in SO mode.
  // Auto-populates row.assemblyHint with the Layer B response so the qty input
  // can render a hint and warn on override. Cached per (rowId, partId) in a ref
  // to avoid refetching when other row fields change. Resets when the row's
  // part changes (different key → cache miss → refetch).
  const assemblyHintCacheRef = useRef(new Map());
  const assemblyHintKey = useMemo(
    () => bulkRows.map(r => `${r.id}:${r.part?.part_id || ''}`).join('|'),
    [bulkRows]
  );
  useEffect(() => {
    if (entryMode !== "sales_order" || !selectedSalesOrderId) return;

    let cancelled = false;
    bulkRows.forEach(async (row) => {
      if (!row.part?.part_id) return;
      const key = `${row.id}:${row.part.part_id}`;
      if (assemblyHintCacheRef.current.has(key)) {
        const cached = assemblyHintCacheRef.current.get(key);
        if (row.assemblyHint !== cached) {
          setBulkRows(prev => prev.map(r => r.id === row.id ? { ...r, assemblyHint: cached } : r));
        }
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/sales_orders/${selectedSalesOrderId}/expected_job_qty?part_id=${row.part.part_id}`);
        if (cancelled) return;
        let result = null;
        if (res.ok) {
          const data = await res.json();
          if (data.applicable) result = data;
        }
        assemblyHintCacheRef.current.set(key, result);
        setBulkRows(prev => prev.map(r =>
          r.id === row.id && r.part?.part_id === row.part.part_id
            ? { ...r, assemblyHint: result }
            : r
        ));
      } catch (e) {
        if (cancelled) return;
        assemblyHintCacheRef.current.set(key, null);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblyHintKey, selectedSalesOrderId, entryMode]);

  // Per-part In Prod on the selected order, used by the per-row assembly-qty
  // hint to show "remaining" and to color the entry. A part on several lines (a
  // component shared by two assemblies appears under both assembly_group
  // blocks): /truth splits its In Prod across them, so the part's figure is the
  // SUM of the shares (it used to stamp the whole pool on every line, read with
  // max). inProductionByPart is the same number SalesOrderDashboard's merged row
  // shows. See jobq-ui CLAUDE.md "SO fulfillment: /truth splits a part across its lines".
  const partInProductionMap = useMemo(() => {
    if (!soDetails?.lines) return new Map();
    const flat = soDetails.lines.flatMap(item => (
      item.type === 'item' ? [item.data]
        : item.type === 'assembly_group' ? (item.components || [])
        : []
    ));
    return inProductionByPart(flat);
  }, [soDetails]);

  // Part ids on the selected sales order's lines: the bulk grid marks a shared cut sheet's parts
  // that belong to OTHER orders (they must be allocated there). Empty outside Sales Order mode.
  const orderPartIds = useMemo(() => {
    const ids = new Set();
    if (entryMode !== "sales_order" || !soDetails?.lines) return ids;
    soDetails.lines.forEach(item => {
      if (item.type === 'item' && item.data?.part_id) ids.add(item.data.part_id);
      else if (item.type === 'assembly_group') (item.components || []).forEach(c => c.part_id && ids.add(c.part_id));
    });
    return ids;
  }, [soDetails, entryMode]);

  // Pending allocations referencing parts that have no existing active job on
  // the source SO yet. These CANNOT be saved standalone — the backend requires
  // a job to attach the allocation to. The user must click "Create Jobs"
  // instead, which creates the jobs and allocations together. Returns the
  // subset of pendingSurplusAllocations that block a standalone save.
  const unsavablePendingAllocations = (() => {
    if (!pendingSurplusAllocations || pendingSurplusAllocations.length === 0) return [];
    const existingProducingPartIds = new Set();
    (soDetails?.orphan_productions || []).forEach(o => {
      if (o.part_id) existingProducingPartIds.add(o.part_id);
    });
    (soDetails?.lines || []).forEach(item => {
      if (item.type === 'item' && item.data?.part_id && (item.data.qty_in_production || 0) > 0) {
        existingProducingPartIds.add(item.data.part_id);
      } else if (item.type === 'assembly_group') {
        (item.components || []).forEach(c => {
          if (c.part_id && (c.qty_in_production || 0) > 0) existingProducingPartIds.add(c.part_id);
        });
      }
    });
    return pendingSurplusAllocations.filter(a => !existingProducingPartIds.has(a.part_id));
  })();

  // Post pending surplus allocations to the backend. For each allocation:
  //   - If jobIdByPartId[part_id] is set (newly-created job), attach that job_id.
  //   - Otherwise, send job_id=null + source_so_id so the backend auto-resolves an
  //     existing job on the source SO that produces this part.
  // Returns { ok, count, error } without mutating state on failure so callers can retry.
  async function postPendingAllocations(jobIdByPartId = {}) {
    if (!pendingSurplusAllocations || pendingSurplusAllocations.length === 0) {
      return { ok: true, count: 0 };
    }
    if (!selectedSalesOrderId) {
      return { ok: false, count: 0, error: "No Sales Order selected" };
    }
    const soId = parseInt(selectedSalesOrderId);
    const payload = pendingSurplusAllocations.map(a => ({
      job_id: jobIdByPartId[a.part_id] || null,
      source_so_id: soId,
      part_id: a.part_id,
      source_so_line_id: a.source_so_line_id != null ? a.source_so_line_id : null,
      target_so_id: a.target_so_id,
      target_so_line_id: a.target_so_line_id,
      quantity: a.quantity
    }));
    try {
      const res = await fetch(`${API_BASE}/surplus_allocations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allocations: payload })
      });
      if (!res.ok) {
        const errText = await res.text();
        return { ok: false, count: 0, error: errText };
      }
      await res.json();
      return { ok: true, count: payload.length };
    } catch (err) {
      return { ok: false, count: 0, error: String(err) };
    }
  }

  // Save button handler: POSTs pending allocations without creating new jobs,
  // then refreshes SO truth so the Sales Order Fulfillment table updates.
  async function handleSavePendingAllocations() {
    if (pendingSurplusAllocations.length === 0) return;
    if (unsavablePendingAllocations.length > 0) {
      const partIds = unsavablePendingAllocations.map(a => `#${a.part_id}`).join(', ');
      alert(
        `Cannot save allocations yet — ${unsavablePendingAllocations.length} allocation(s) reference parts that have no active job on this SO yet (${partIds}). ` +
        `Click "Create Jobs" below instead; that path creates the jobs and saves the allocations together.`
      );
      return;
    }
    const result = await postPendingAllocations({});
    if (result.ok) {
      setPendingSurplusAllocations([]);
      alert(`Saved ${result.count} allocation(s).`);
      if (entryMode === "sales_order" && selectedSalesOrderId) {
        try {
          const refreshRes = await fetch(`${API_BASE}/sales_orders/${selectedSalesOrderId}/truth`);
          if (refreshRes.ok) setSoDetails(await refreshRes.json());
        } catch (err) {
          console.error("Failed to refresh SO details after saving allocations:", err);
        }
      }
    } else {
      alert(`Failed to save allocations: ${result.error}`);
    }
  }

  async function handleCreateBulkJobs() {
    setLoading(true);
    setError(null);
    setBulkCreatedJobs([]);

    if (!bulkCustomerId) {
      setError("Please select a Customer for the jobs.");
      setLoading(false);
      return;
    }
    
    // NEW: Validation for Sales Order Mode
    if (entryMode === "sales_order" && !selectedSalesOrderId) {
        setError("Please select a Sales Order to link these jobs to.");
        setLoading(false);
        return;
    }
    // A priced T&M order takes no new jobs (the API refuses them too).
    const tmBlocked = tmJobEntryGate(entryMode, soDetails?.header).createBlockedReason;
    if (tmBlocked) {
        setError(tmBlocked);
        setLoading(false);
        return;
    }

    // Check for blocking errors in the grid UI
    const blockingErrors = bulkRows.some(r => r.error && (r.error.includes("Archived") || r.error.includes("obsolete")));
    if (blockingErrors) {
        setError("Some rows have invalid cut sheets. Please resolve errors before submitting.");
        setLoading(false);
        return;
    }

    const createdEntries = [];
    // Failures are tracked HERE, in a plain local, and never read back out of state.
    // The per-row text goes to state via handleBulkRowChange -> setBulkRows(prev => ...),
    // which does NOT update the `bulkRows` this running function closed over, so the old
    // `bulkRows.some(r => r.error)` below always read the PRE-LOOP snapshot. It reported
    // success on every first run regardless of what failed, and left the
    // `if (!anyErrors) setBulkRows([])` at the end free to clear the grid that held the
    // only copy of the error. SO-1117 on 2026-09-01 is the case: two jobs were created,
    // rolled back by the guard below, and reported as "All jobs created successfully!"
    // with the grid wiped behind them, so the operator had nothing to go on.
    const failedRows = [];
    const failureLabel = (row, i) => row.part?.product_id || `row ${i + 1}`;
    // T&M load hints of the jobs that were kept (a fixed-price job's answer has none).
    const tmHints = [];
    try {
        let newPoId = null;
        if (poFile && bulkCustomerId) {
            newPoId = await uploadPOFile(bulkCustomerId);
        }
        const finalPoId = newPoId || (customerPO ? customerPO.document_id : null);

        for (let i = 0; i < bulkRows.length; i++) {
          const row = bulkRows[i];

          const rowType = row.part?.stock_type || 'Sheet Stock';

          // Tracked outside the try so the rollback guard below can clean up a
          // job header whose cut-source attach failed.
          let createdJob = null;
          let rowError = null;
          let rowTmHints = null;

          try {
            const hasSheetStockError = rowType === 'Sheet Stock' && (!row.part || !row.cutSheet || !row.quantity);
            const hasShapeStockError = rowType === 'Shape Stock' && (!row.part || !row.cutList || !row.quantity);

            if (hasSheetStockError || hasShapeStockError) throw new Error("Missing part, cut source, or quantity.");
            // Defensive: a non-custom row whose part type isn't Sheet/Shape Stock
            // has no cut-source branch below, so it would create a header we can
            // never attach parts to — a guaranteed empty job. Block it up front.
            if (!row.isCustom && rowType !== 'Sheet Stock' && rowType !== 'Shape Stock') {
                throw new Error(`Unsupported stock type "${rowType}" — select a part with a cut sheet or cut list.`);
            }
            if (!row.dueDate) throw new Error("A due date is required for each job row.");

            const createPayload = {
              job_type: row.isCustom ? "Custom" : "Production",
              job_category: rowType,
              customer_id: bulkCustomerId,
              job_description: row.part.description,
              customer_po: bulkPoNumber || null,
              customer_so: bulkSoNumber || null,
              shipping_method: bulkShippingMethod,
              priority_level: row.jobPriority,
              due_date: row.dueDate || null,
              notes: row.notes || "",
              sales_order_id: entryMode === "sales_order" ? parseInt(selectedSalesOrderId) : null
            };

            const createRes = await fetch(`${API_BASE}/jobs`, {
              method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(createPayload)
            });
            if (!createRes.ok) throw new Error(await apiErrorMessage(createRes));
            createdJob = await createRes.json();

            if (finalPoId) {
                await linkPOToJob(createdJob.job_id, finalPoId);
            }

            const qtyNum = parseInt(row.quantity, 10);
            let addRes;

            if (rowType === 'Sheet Stock') {
                const addSheetPayload = { part_id: row.part.part_id, cut_sheet_id: row.cutSheet.cut_sheet_id, requested_quantity: qtyNum };
                addRes = await fetch(`${API_BASE}/jobs/${createdJob.job_id}/add_cut_sheet`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(addSheetPayload)
                });
            } else if (rowType === 'Shape Stock') {
                const addListPayload = { part_id: row.part.part_id, cut_list_id: row.cutList.cut_list_id, requested_quantity: qtyNum };
                addRes = await fetch(`${API_BASE}/jobs/${createdJob.job_id}/add_cut_list`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(addListPayload)
                });
            }

            if (addRes && !addRes.ok) throw new Error(await apiErrorMessage(addRes));
            // T&M: overrun / "is this a remake of defect #X?" / hints error (null for fixed-price).
            if (addRes) rowTmHints = loadHintsFrom(await readJsonQuietly(addRes), createdJob);

            if (row.authorizedUserId) {
                try {
                    await fetch(`${API_BASE}/jobs/${createdJob.job_id}/authorized_user`, {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ authorized_user_id: parseInt(row.authorizedUserId, 10) })
                    });
                } catch (err) {
                    console.warn(`Failed to set authorized user on job ${createdJob.job_id}:`, err);
                }
            }
          } catch (err) {
            rowError = err.message;
          }

          // Rollback guard: job creation is two-phase (POST /jobs header, then
          // add_cut_sheet/add_cut_list attaches the part + explodes tasks). If
          // phase 2 threw — or attached nothing (e.g. assembly-qty 400, part not
          // on the sheet) — the header is an empty orphan with no tasks (the
          // job-7338 bug). Verify the job actually has parts; if not, DELETE the
          // empty header (FK-safe: a header with parts/tasks can't be deleted) so
          // it never lingers in the queue.
          if (createdJob?.job_id) {
            let jobParts = [];
            try {
                const partsRes = await fetch(`${API_BASE}/jobs/${createdJob.job_id}/parts`);
                jobParts = partsRes.ok ? await partsRes.json() : [];
            } catch (e) { jobParts = []; }

            if (jobParts.length === 0) {
                try { await fetch(`${API_BASE}/jobs/${createdJob.job_id}`, { method: "DELETE" }); } catch (e) { /* best effort */ }
                const rolledBackMsg = (rowError || "Cut sheet could not be attached") + " — empty job rolled back.";
                handleBulkRowChange(row.id, "error", rolledBackMsg);
                failedRows.push({ label: failureLabel(row, i), message: rolledBackMsg });
            } else {
                handleBulkRowChange(row.id, "error", rowError);
                if (rowError) failedRows.push({ label: failureLabel(row, i), message: rowError });
                createdEntries.push({ job: createdJob, parts: jobParts });
                if (rowTmHints) tmHints.push(rowTmHints);
            }
          } else {
            handleBulkRowChange(row.id, "error", rowError);
            if (rowError) failedRows.push({ label: failureLabel(row, i), message: rowError });
          }
        }
    } catch (err) {
        setError("Global error during bulk creation: " + err.message);
    }

    setLoading(false);
    
    if (createdEntries.length > 0) {
        const jobIds = createdEntries.map(entry => entry.job.job_id);
        try {
            const queryParams = new URLSearchParams();
            jobIds.forEach(id => queryParams.append('job_ids', id));
            const url = `${API_BASE}/export/bulk-order-summary?${queryParams.toString()}`;
            const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
            if (!response.ok) throw new Error('Failed to download bulk order summary.');
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            const date = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '');
            a.download = `bulk_order_summary_${date}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.error(err);
            setError("Failed to export the bulk order summary.");
        }
    }   
    const anyErrors = failedRows.length > 0;
    alert(anyErrors
      ? `${failedRows.length} row(s) FAILED — no job exists for these:\n\n`
        + failedRows.map(f => `• ${f.label}: ${f.message}`).join("\n")
        + `\n\n${createdEntries.length} job(s) created successfully.`
      : "All jobs created successfully!");
    createdEntries.sort((a, b) => (a.job.master_job_num || 0) - (b.job.master_job_num || 0));
    setBulkCreatedJobs(createdEntries);
    if (tmHints.length > 0) {
        setTmLoadHints({
            soId: entryMode === "sales_order" && selectedSalesOrderId ? parseInt(selectedSalesOrderId, 10) : null,
            orderNumber: soDetails?.header?.order_number || null,
            entries: tmHints,
        });
    }

    // POST surplus allocations if any were configured. Allocations added against
    // newly-staged rows are attributed to the new job_id via jobIdByPartId; those
    // added against existing jobs (same-line surplus on an in-production part, or
    // orphan parts produced by previously-created jobs) pass job_id=null and the
    // backend auto-resolves from source_so_id + part_id.
    if (pendingSurplusAllocations.length > 0) {
        const jobIdByPartId = {};
        createdEntries.forEach(entry => {
            if (entry.parts) {
                entry.parts.forEach(p => {
                    if (!jobIdByPartId[p.part_id]) {
                        jobIdByPartId[p.part_id] = entry.job.job_id;
                    }
                });
            }
        });

        const result = await postPendingAllocations(jobIdByPartId);
        if (result.ok) {
            setPendingSurplusAllocations([]);
            if (result.count > 0) {
                console.log(`Created ${result.count} surplus allocation(s)`);
            }
        } else {
            console.error("Failed to create surplus allocations:", result.error);
            setError(`Jobs created, but failed to save surplus allocations: ${result.error}`);
        }
    }

    // Refresh SO Details if in SO mode
    if (entryMode === "sales_order" && selectedSalesOrderId) {
        const refreshRes = await fetch(`${API_BASE}/sales_orders/${selectedSalesOrderId}/truth`);
        if(refreshRes.ok) setSoDetails(await refreshRes.json());
        if(!anyErrors) setBulkRows([]);
    }
  }

  const renderPOViewer = () => {
    if (!customerPO) return null;
    const docURL = customerPODocUrl || customerPO.s3_url;
    const isPDF = customerPO.s3_key.toLowerCase().endsWith(".pdf");
    return (
      <div className="card sticky-top" style={{ top: '1rem' }}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Customer PO #{customerPO.document_id}: {customerPO.original_filename}</span>
          <button className="btn-close" onClick={() => navigate('/customer-po-queue')}></button>
        </div>
        <div className="card-body">
          {!docURL ? (
            <div className="text-muted small">Loading the PO document…</div>
          ) : isPDF ? (
            <embed src={docURL} type="application/pdf" width="100%" height="700px" />
          ) : (
            <img src={docURL} alt="PO Preview" style={{ maxWidth: '100%', height: 'auto' }} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid my-4">
      <div className="row">
        {/* Main form content */}
        <div className="col-md-8">
          <h3>Job Entry</h3>
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mb-3">
            <label>Entry Mode</label>
            <select className="form-select" value={entryMode} onChange={e => {
                setEntryMode(e.target.value);
                if(e.target.value !== 'sales_order') {
                    setSalesOrderList([]);
                    setSelectedSalesOrderId("");
                }
            }}>
              <option value="single">Single Job</option>
              <option value="bulk">Multiple (Bulk) Jobs</option>
              <option value="sales_order">Sales Order Fulfillment</option>
            </select>
          </div>

          {entryMode === 'single' && (
            <div className="mb-3">
              <label>Job Category</label>
              <select className="form-select" value={jobCategory} onChange={e => setJobCategory(e.target.value)}>
                <option value="Sheet Stock">Sheet Stock Job</option>
                <option value="Shape Stock">Shape Stock Job</option>
              </select>
            </div>
          )}

          {/* SINGLE MODE */}
          {entryMode === "single" && !assemblyJobId && (
            <SingleJobForm
                jobCategory={jobCategory}
                jobType={jobType} setJobType={setJobType}
                customerId={customerId} setCustomerId={setCustomerId}
                poNumber={poNumber} setPoNumber={setPoNumber}
                soNumber={soNumber} setSoNumber={setSoNumber}
                shippingMethod={shippingMethod} setShippingMethod={setShippingMethod}
                jobDescription={jobDescription} setJobDescription={setJobDescription}
                priority={priority} setPriority={setPriority}
                dueDate={dueDate} setDueDate={setDueDate}
                notes={notes} setNotes={setNotes}
                handleCreateJob={handleSingleJobCreate}
                customerList={customerList} materialList={materialList}
                productionPartList={productionPartList} cutSheetList={cutSheetList}
                selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                selectedCutSheet={selectedCutSheet} setSelectedCutSheet={setSelectedCutSheet}
                requestedQuantity={requestedQuantity} setRequestedQuantity={setRequestedQuantity}
                tempRequestedQuantity={tempRequestedQuantity} setTempRequestedQuantity={setTempRequestedQuantity}
                productionPreviewParts={productionPreviewParts}
                productionSheetsNeeded={productionSheetsNeeded}
                productionEstimatedLaserHrs={productionEstimatedLaserHrs}
                selectedMaterial={selectedMaterial} setSelectedMaterial={setSelectedMaterial}
                customChosenParts={customChosenParts} setCustomChosenParts={setCustomChosenParts}
                useCustomMaterial={useCustomMaterial} setUseCustomMaterial={setUseCustomMaterial}
                customMaterialDesc={customMaterialDesc} setCustomMaterialDesc={setCustomMaterialDesc}
                customSheetsNeeded={customSheetsNeeded} setCustomSheetsNeeded={setCustomSheetsNeeded}
                customEstimatedLaserHrs={customEstimatedLaserHrs} customPreviewParts={customPreviewParts}
                loading={loading} filteredProductionPartsSingle={filteredProductionPartsSingle}
                customCutSheetImage={customCutSheetImage} setCustomCutSheetImage={setCustomCutSheetImage}
                customLaserMinutes={customLaserMinutes} setCustomLaserMinutes={setCustomLaserMinutes}
                cutListList={cutListList} selectedCutList={selectedCutList} setSelectedCutList={setSelectedCutList}
                shapeRequestedQuantity={shapeRequestedQuantity} setShapeRequestedQuantity={setShapeRequestedQuantity}
                tempShapeRequestedQuantity={tempShapeRequestedQuantity} setTempShapeRequestedQuantity={setTempShapeRequestedQuantity}
                shapeStockPreviewParts={shapeStockPreviewParts} sticksNeeded={sticksNeeded}
                maintenanceWorkstation={maintenanceWorkstation} setMaintenanceWorkstation={setMaintenanceWorkstation}
                poFile={poFile} setPoFile={setPoFile} customerPO={customerPO}
                assemblyList={assemblyList}
                targetAssembly={targetAssembly}
                setTargetAssembly={setTargetAssembly}
                buildQty={buildQty}
                setBuildQty={setBuildQty}
                handleAssemblyQtyBlur={handleAssemblyQtyBlur}
                recipeInfo={recipeInfo}
                yieldAlert={yieldAlert}
            />
          )}

          {/* UNIFIED ASSEMBLY SUB-MODE */}
          {entryMode === "single" && assemblyJobId && (
            <UnifiedAssemblyComposer
                jobId={assemblyJobId} 
                customerId={customerId} 
                productionPartList={productionPartList}
                cutSheetList={cutSheetList} 
                cutListList={cutListList}
                materialList={materialList}
                targetAssembly={targetAssembly}
                buildQty={buildQty}
                recipeInfo={recipeInfo}
            />
          )}

          {/* SALES ORDER MODE SPECIFIC UI */}
          {entryMode === "sales_order" && (
            <div className="mb-4 p-3 border rounded bg-light">
                <h5 className="text-primary">Sales Order Selection</h5>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label">Customer</label>
                        <select className="form-select" value={bulkCustomerId || ""} onChange={(e) => setBulkCustomerId(e.target.value)}>
                          <option value="">-- Select Customer --</option>
                          {customerList.map((c) => (<option key={c.customer_id} value={c.customer_id}>{c.name}</option>))}
                        </select>
                    </div>
                    <div className="col-md-6">
                        {(() => {
                            const CLOSED_LIFECYCLES = new Set(['Shipped', 'Invoiced', 'Completed']);
                            const fulfillableSOs = salesOrderList.filter(
                                so => !CLOSED_LIFECYCLES.has(so.lifecycle_stage)
                            );
                            const hiddenClosedCount = salesOrderList.length - fulfillableSOs.length;
                            return (
                                <>
                                    <label className="form-label">
                                        Open Sales Order
                                        {hiddenClosedCount > 0 && (
                                            <span
                                                className="text-muted ms-2"
                                                style={{ fontSize: '0.85em' }}
                                                title="Sales orders that are fully shipped or fully invoiced are hidden from this list to reduce noise. Open the SO directly from the Sales Orders page if you need to add work to a closed order."
                                            >
                                                ({hiddenClosedCount} fully shipped/invoiced hidden)
                                            </span>
                                        )}
                                    </label>
                                    <select className="form-select" value={selectedSalesOrderId} onChange={(e) => setSelectedSalesOrderId(e.target.value)} disabled={!bulkCustomerId}>
                                        <option value="">-- Select Order --</option>
                                        {fulfillableSOs.map(so => (
                                            <option key={so.sales_order_id} value={so.sales_order_id}>
                                                {so.order_number} ({(() => {
                                                  const bits = [];
                                                  if (so.customer_po) bits.push(`PO: ${so.customer_po}`);
                                                  if (so.customer_so) bits.push(`SO: ${so.customer_so}`);
                                                  return bits.length ? bits.join(' · ') : new Date(so.created_at).toLocaleDateString();
                                                })()})
                                            </option>
                                        ))}
                                    </select>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* Dashboard visualization of progress */}
                {soDetails && (
                    <div className="mt-3">
                        <ErrorBoundary label="The T&M panel">
                          <TmJobEntryPanel header={soDetails.header} />
                        </ErrorBoundary>
                        <SalesOrderDashboard
                            soDetails={soDetails}
                            bulkRows={bulkRows}
                            productionPartList={productionPartList}
                            onAddPartToBatch={handleAddPartFromSO}
                            jobCategory={jobCategory}
                            pendingSurplusAllocations={pendingSurplusAllocations}
                            onAllocateSurplus={!tmJobEntryGate(entryMode, soDetails.header).allowAllocate ? null : (line) => {
                                setSurplusModalContext({
                                    part_id: line.part_id,
                                    part_product_id: line.product_id,
                                    part_description: line.description,
                                    surplus: line.unallocatedSurplus,
                                    source_so_line_id: line.sales_order_line_id,
                                    // Same-customer surplus — default target customer to source
                                    part_customer_id: bulkCustomerId ? parseInt(bulkCustomerId) : null
                                });
                                setShowSurplusModal(true);
                            }}
                        />

                        {/* Cross-SO orphan parts (from mixed-customer cut sheets) */}
                        <OtherPartsBeingProducedTable
                            soDetails={soDetails}
                            bulkRows={bulkRows}
                            productionPartList={productionPartList}
                            customerList={customerList}
                            pendingSurplusAllocations={pendingSurplusAllocations}
                            onAllocateOrphan={(orphan) => {
                                setSurplusModalContext({
                                    part_id: orphan.part_id,
                                    part_product_id: orphan.part_product_id,
                                    part_description: orphan.part_description,
                                    part_customer_id: orphan.part_customer_id,
                                    part_customer_name: orphan.part_customer_name,
                                    unallocated: orphan.unallocated,
                                    source_so_line_id: null
                                });
                                setShowSurplusModal(true);
                            }}
                        />

                        {/* Save pending allocations without creating new jobs.
                            Useful when the user is allocating surplus/orphans from
                            jobs that already exist in the system. When bulkRows has
                            staged jobs, allocations still flow through Create Jobs,
                            but this button is also available as a fallback. */}
                        {pendingSurplusAllocations.length > 0 && (
                            <div className="card mb-4 shadow-sm border-info">
                                <div className="card-body py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong className="text-info">
                                                <i className="bi bi-clock-history me-1"></i>
                                                {pendingSurplusAllocations.length} pending allocation(s)
                                            </strong>
                                            <div className="small text-muted mt-1">
                                                {pendingSurplusAllocations.map((a, i) => (
                                                    <span key={i} className="me-2">
                                                        {a.quantity} × part #{a.part_id}
                                                        {a.target_so_number ? ` → ${a.target_so_number}` : ''}
                                                        {i < pendingSurplusAllocations.length - 1 ? ',' : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setPendingSurplusAllocations([])}
                                                title="Discard pending allocations"
                                            >
                                                <i className="bi bi-x-lg me-1"></i>
                                                Clear
                                            </button>
                                            <button
                                                className="btn btn-info"
                                                onClick={handleSavePendingAllocations}
                                                disabled={loading || unsavablePendingAllocations.length > 0}
                                                title={
                                                    unsavablePendingAllocations.length > 0
                                                        ? `Can't save — ${unsavablePendingAllocations.length} allocation(s) reference parts with no job yet. Use "Create Jobs" below instead.`
                                                        : "Save allocations now without creating new jobs"
                                                }
                                            >
                                                <i className="bi bi-save me-1"></i>
                                                Save Allocations Now
                                            </button>
                                        </div>
                                    </div>
                                    {unsavablePendingAllocations.length > 0 && (
                                        <div className="alert alert-warning mt-3 mb-0 py-2 small">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            <strong>{unsavablePendingAllocations.length} allocation(s) can't be saved standalone</strong> —
                                            part{unsavablePendingAllocations.length > 1 ? 's' : ''}{' '}
                                            {unsavablePendingAllocations.map(a => `#${a.part_id}`).join(', ')}{' '}
                                            {unsavablePendingAllocations.length > 1 ? 'have' : 'has'} no active job on this SO yet.
                                            Click <strong>Create Jobs</strong> below to save jobs and allocations together.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
          )}

          {/* BULK OR SALES ORDER MODE USES BULK CREATOR */}
          {(entryMode === "bulk" || (entryMode === "sales_order" && selectedSalesOrderId)) && (
            <>
              {/* --- TOGGLE FOR PART VS CUT SHEET SET ENTRY --- */}
              <div className="mb-3">
                  <label className="form-label me-3 fw-bold">Creation Method:</label>
                  <div className="btn-group">
                      <button className={`btn ${fulfillmentMethod === 'part' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFulfillmentMethod('part')}>By Part (Grid)</button>
                      <button className={`btn ${fulfillmentMethod === 'set' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFulfillmentMethod('set')}>By Cut Sheet Set</button>
                  </div>
              </div>

              {/* RENDER THE SELECTED CREATOR COMPONENT */}
              {fulfillmentMethod === 'part' ? (
                <BulkJobCreator
                  entryMode={entryMode}
                  jobCategory={jobCategory} bulkRows={bulkRows} setBulkRows={setBulkRows}
                  bulkCustomerId={bulkCustomerId} setBulkCustomerId={setBulkCustomerId}
                  bulkPoNumber={bulkPoNumber} setBulkPoNumber={setBulkPoNumber}
                  bulkSoNumber={bulkSoNumber} setBulkSoNumber={setBulkSoNumber}
                  bulkShippingMethod={bulkShippingMethod} setBulkShippingMethod={setBulkShippingMethod}
                  handleAddBulkRow={handleAddBulkRow} handleDuplicateLastRow={handleDuplicateLastRow}
                  handleCreateBulkJobs={handleCreateBulkJobs}
                  materialList={materialList} productionPartList={productionPartList}
                  cutSheetList={cutSheetList} cutListList={cutListList} customerList={customerList}
                  loading={loading} error={error} filteredProductionPartsBulk={filteredProductionPartsBulk}
                  handleBulkRowChange={handleBulkRowChange}
                  handleRemoveBulkRow={handleRemoveBulkRow}
                  poFile={poFile} setPoFile={setPoFile} customerPO={customerPO}
                  onOpenCustomModal={() => setShowCustomCutSheetModal(true)}
                  usersList={usersList}
                  partInProductionMap={partInProductionMap}
                  orderPartIds={orderPartIds}
                  createBlockedReason={tmJobEntryGate(entryMode, soDetails?.header).createBlockedReason}
                />
              ) : (
                <CutSheetSetJobCreator 
                  cutSheetSets={cutSheetSets}
                  onStage={handleStageSetToGrid}
                />
              )}
            </>
          )}

          {bulkCreatedJobs.length > 0 && (
            <BulkCreationSummary createdJobs={bulkCreatedJobs} customerList={customerList} />
          )}
        </div>

        <div className="col-md-4">
          {renderPOViewer()}
        </div>
      </div>

      {/* Surplus Allocation Modal */}
      <SurplusAllocationModal
        show={showSurplusModal}
        onClose={() => { setShowSurplusModal(false); setSurplusModalContext(null); }}
        context={surplusModalContext}
        currentSoId={selectedSalesOrderId}
        sourceCustomerId={bulkCustomerId ? parseInt(bulkCustomerId) : null}
        customerList={customerList}
        onConfirm={(newAllocations) => {
          setPendingSurplusAllocations(prev => [...prev, ...newAllocations]);
        }}
      />

      {/* Custom Cut Sheet Modal */}
      <CustomCutSheetModal
        show={showCustomCutSheetModal}
        onClose={() => setShowCustomCutSheetModal(false)}
        onSubmit={handleCustomCutSheetSubmit}
        bulkCustomerId={bulkCustomerId}
        materialList={materialList}
        customerList={customerList}
        jobCategory={jobCategory}
        soDetails={entryMode === 'sales_order' ? soDetails : null}
      />

      {/* Time & Materials: overrun + "is this a remake of defect #X?" after a job load (T&M jobs only). */}
      {tmLoadHints && (
        <ErrorBoundary label="The T&M job-load checks (the jobs themselves were created)">
          <TmJobLoadHintsModal
            show
            salesOrderId={tmLoadHints.soId}
            orderNumber={tmLoadHints.orderNumber}
            entries={tmLoadHints.entries}
            onClose={() => setTmLoadHints(null)}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

// =================================================================================================
// NEW COMPONENT: Cut Sheet Set Job Creator
// =================================================================================================
function CutSheetSetJobCreator({
    cutSheetSets,
    onStage // <--- Change 'onSuccess' etc to a simple 'onStage' prop
}) {
    const [selectedSetId, setSelectedSetId] = useState("");
    const [setQuantity, setSetQuantity] = useState(1);

    const handleStageClick = () => {
        if (!selectedSetId) return alert("Please select a Cut Sheet Set.");
        if (setQuantity <= 0) return alert("Set Quantity must be positive.");
        
        // Pass data back to parent to populate grid
        onStage(selectedSetId, setQuantity);
        
        // Reset local state
        setSetQuantity(1);
        setSelectedSetId("");
    };

    return (
        <div style={{ border: "1px solid #ccc", padding: "1.5rem", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
            <h5 className="mb-3">Stage Jobs from Cut Sheet Set</h5>
            
            <div className="row g-3">
                <div className="col-md-8">
                    <label className="form-label fw-bold">Select Cut Sheet Set</label>
                    <select 
                        className="form-select"
                        value={selectedSetId}
                        onChange={e => setSelectedSetId(e.target.value)}
                    >
                        <option value="">-- Select Set --</option>
                        {cutSheetSets.map(set => (
                            <option key={set.cut_sheet_set_id} value={set.cut_sheet_set_id}>
                                {set.set_name} {set.description ? `(${set.description})` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="col-md-4">
                    <label className="form-label fw-bold">Set Quantity (Multiplier)</label>
                    <input 
                        type="number" 
                        className="form-control"
                        min="1"
                        value={setQuantity}
                        onChange={e => setSetQuantity(e.target.value)}
                    />
                </div>

                <div className="col-12 mt-4">
                    <button 
                        className="btn btn-primary w-100 py-2 fw-bold"
                        onClick={handleStageClick}
                    >
                        <i className="bi bi-list-check me-2"></i>
                        Stage in Grid for Review
                    </button>
                    <div className="alert alert-info mt-3 small mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>How this works:</strong> Clicking "Stage" will calculate all required cut sheets based on the set and multiplier, then add them as rows to the table above. You can review them before creating the jobs.
                    </div>
                </div>
            </div>
        </div>
    );
}

function SingleJobForm({
  jobCategory,
  jobType, setJobType, customerId, setCustomerId, poNumber, setPoNumber, soNumber, setSoNumber,
  shippingMethod, setShippingMethod,
  jobDescription, setJobDescription, priority, setPriority, dueDate, setDueDate,
  notes, setNotes, handleCreateJob, customerList, materialList, productionPartList,
  cutSheetList, selectedPart, setSelectedPart, selectedCutSheet, setSelectedCutSheet,
  requestedQuantity, setRequestedQuantity, tempRequestedQuantity, setTempRequestedQuantity,
  productionPreviewParts, productionSheetsNeeded, productionEstimatedLaserHrs,
  selectedMaterial, setSelectedMaterial, customChosenParts, setCustomChosenParts,
  useCustomMaterial, setUseCustomMaterial, customMaterialDesc, setCustomMaterialDesc,
  customSheetsNeeded, setCustomSheetsNeeded, customEstimatedLaserHrs, customPreviewParts,
  loading, filteredProductionPartsSingle, customCutSheetImage, setCustomCutSheetImage,
  customLaserMinutes, setCustomLaserMinutes,
  cutListList, selectedCutList, setSelectedCutList, shapeRequestedQuantity, setShapeRequestedQuantity,
  tempShapeRequestedQuantity, setTempShapeRequestedQuantity, shapeStockPreviewParts, sticksNeeded,
  maintenanceWorkstation, setMaintenanceWorkstation,
  poFile, setPoFile, customerPO,
  // NEW PROPS FOR ASSEMBLY
  assemblyList, targetAssembly, setTargetAssembly, buildQty, setBuildQty,
  handleAssemblyQtyBlur, recipeInfo, yieldAlert
}) {
  const [partSearch, setPartSearch] = useState("");

  useEffect(() => {
    if (selectedPart) {
      setPartSearch(selectedPart.product_id);
    } else {
      setPartSearch("");
    }
  }, [selectedPart]);

  return (
    <div>
      <div className="mb-3">
        <label>Job Type</label>
        <select className="form-select" value={jobType} onChange={(e) => setJobType(e.target.value)}>
          <option value="Production">Production</option>
          {jobCategory === 'Sheet Stock' && <option value="Custom">Custom</option>}
          <option value="Assembly">Assembly</option>
          {jobCategory === 'Sheet Stock' && <option value="Maintenance">Maintenance</option>}
        </select>
      </div>

      {jobType !== "Maintenance" && (
        <>
          <div className="mb-3">
            <label>Customer</label>
            <select className="form-select" value={customerId || ""} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">-- Select Customer --</option>
              {customerList.map((c) => (<option key={c.customer_id} value={c.customer_id}>{c.name}</option>))}
            </select>
          </div>
          <div className="mb-3">
            <label>Customer PO (Text)</label>
            <input type="text" className="form-control" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          </div>
          <div className="mb-3">
            <label>Cust SO</label>
            <input type="text" className="form-control" value={soNumber} onChange={(e) => setSoNumber(e.target.value)} />
          </div>

          {!customerPO && (
            <div className="mb-3">
                <label>Upload Customer PO (PDF/Image)</label>
                <input 
                    type="file" 
                    className="form-control" 
                    accept="application/pdf,image/*" 
                    onChange={(e) => setPoFile(e.target.files[0])} 
                />
                <small className="text-muted">If selected, this file will be uploaded and linked to the new job automatically.</small>
            </div>
          )}
          {customerPO && (
             <div className="mb-3 alert alert-info">
                <strong>Linked PO:</strong> {customerPO.original_filename}
             </div>
          )}

          {/* NEW ASSEMBLY CONFIGURATION UI */}
          {jobType === "Assembly" && (
            <div className="p-3 mb-3 bg-light border rounded">
              <h6 className="fw-bold text-primary">Assembly Configuration</h6>
              <div className="row">
                <div className="col-md-8">
                  <label>Select Assembly to Build</label>
                  <select 
                    className="form-select" 
                    value={targetAssembly?.assembly_id || ""}
                    onChange={(e) => {
                      const asm = assemblyList.find(a => a.assembly_id === parseInt(e.target.value));
                      setTargetAssembly(asm);
                      // Auto-fill description if empty
                      if(asm && !jobDescription) setJobDescription(asm.assembly_name);
                    }}
                  >
                    <option value="">-- Choose Assembly --</option>
                    {assemblyList
                      .filter(a => {
                        // Filter assemblies by the selected customer if one is chosen
                        if (customerId) {
                          return a.customer_id === parseInt(customerId, 10);
                        }
                        return true;
                      })
                      .map(a => (
                      <option key={a.assembly_id} value={a.assembly_id}>
                        {a.assembly_name} (Primary: {a.primary_part_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label>Quantity to Build</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    min="1"
                    value={buildQty}
                    onChange={(e) => {
                      const qty = parseInt(e.target.value) || 1;
                      setBuildQty(qty);
                      if(targetAssembly) setJobDescription(targetAssembly.assembly_name);
                    }}
                    // NEW: ENFORCE RECIPE ROUNDING
                    onBlur={handleAssemblyQtyBlur}
                  />
                </div>
              </div>
              
              {/* YIELD WARNING ALERT */}
              {yieldAlert && (
                  <div className="alert alert-warning mt-2">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {yieldAlert}
                  </div>
              )}
              
              {/* YIELD INFO BADGE */}
              {recipeInfo && recipeInfo.has_recipe && (
                  <div className="mt-2 text-muted small">
                      <i className="bi bi-info-circle me-1"></i>
                      Production Recipe: <strong>{recipeInfo.recipe_name}</strong> (Yields {recipeInfo.batch_size} units per batch)
                  </div>
              )}
            </div>
          )}

          <div className="mb-3">
            <label>Shipping Method</label>
            <select className="form-select" value={shippingMethod} onChange={(e) => setShippingMethod(e.target.value)}>
              <option value="Customer Pickup">Customer Pickup</option>
              <option value="Delivery">Delivery</option>
              <option value="Freight">Freight</option>
              <option value="Ground Shipping">Ground Shipping</option>
            </select>
          </div>
        </>
      )}

      {jobType === "Maintenance" && (
          <div className="mb-3">
              <label>Workstation for Maintenance</label>
              <select
                  className="form-select"
                  value={maintenanceWorkstation}
                  onChange={(e) => setMaintenanceWorkstation(e.target.value)}
              >
                  <option value="Laser">Laser</option>
                  <option value="Tap">Tap</option>
                  <option value="Brake">Brake</option>
                  <option value="Fabrication">Fabrication</option>
                  <option value="Blast">Blast</option>
                  <option value="Finish">Finish</option>
              </select>
          </div>
      )}

      <div className="mb-3"><label>Job Description</label><input type="text" className="form-control" value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} /></div>
      <div className="mb-3"><label>Priority</label><select className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}><option value={1}>1 (Critical)</option><option value={2}>2 (High)</option><option value={3}>3 (Normal)</option><option value={4}>4 (Low)</option></select></div>
      <div className="mb-3"><label>Due Date</label><input type="date" className="form-control" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
      <div className="mb-3"><label>Notes</label><textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

      {jobCategory === 'Sheet Stock' && jobType === 'Production' && (
          <ProductionSingleJob
            productionPartList={productionPartList}
            cutSheetList={cutSheetList}
            materialList={materialList}
            selectedPart={selectedPart}
            setSelectedPart={setSelectedPart}
            selectedCutSheet={selectedCutSheet}
            setSelectedCutSheet={setSelectedCutSheet}
            requestedQuantity={requestedQuantity}
            setRequestedQuantity={setRequestedQuantity}
            tempRequestedQuantity={tempRequestedQuantity}
            setTempRequestedQuantity={setTempRequestedQuantity}
            productionPreviewParts={productionPreviewParts}
            productionSheetsNeeded={productionSheetsNeeded}
            productionEstimatedLaserHrs={productionEstimatedLaserHrs}
            filteredProductionPartsSingle={filteredProductionPartsSingle}
            partSearch={partSearch}
            setPartSearch={setPartSearch}
          />
      )}
      
      {jobCategory === 'Sheet Stock' && jobType === 'Custom' && (
          <CustomSingleJob
            jobCategory={jobCategory} 
            materialList={materialList}
            selectedMaterial={selectedMaterial}
            setSelectedMaterial={setSelectedMaterial}
            customChosenParts={customChosenParts}
            setCustomChosenParts={setCustomChosenParts}
            useCustomMaterial={useCustomMaterial}
            setUseCustomMaterial={setUseCustomMaterial}
            customMaterialDesc={customMaterialDesc}
            setCustomMaterialDesc={setCustomMaterialDesc}
            customSheetsNeeded={customSheetsNeeded}
            setCustomSheetsNeeded={setCustomSheetsNeeded}
            customEstimatedLaserHrs={customEstimatedLaserHrs}
            customPreviewParts={customPreviewParts}
            customCutSheetImage={customCutSheetImage}
            setCustomCutSheetImage={setCustomCutSheetImage}
            customerList={customerList}
            customLaserMinutes={customLaserMinutes}
            setCustomLaserMinutes={setCustomLaserMinutes}
          />
      )}

      {jobCategory === 'Shape Stock' && jobType === 'Production' && (
          <ShapeStockSingleJob
            productionPartList={productionPartList}
            cutListList={cutListList}
            materialList={materialList}
            selectedPart={selectedPart}
            setSelectedPart={setSelectedPart}
            selectedCutList={selectedCutList}
            setSelectedCutList={setSelectedCutList}
            shapeRequestedQuantity={shapeRequestedQuantity}
            setShapeRequestedQuantity={setShapeRequestedQuantity}
            tempShapeRequestedQuantity={tempShapeRequestedQuantity}
            setTempShapeRequestedQuantity={setTempShapeRequestedQuantity}
            shapeStockPreviewParts={shapeStockPreviewParts}
            sticksNeeded={sticksNeeded}
            filteredProductionPartsSingle={filteredProductionPartsSingle}
            partSearch={partSearch}
            setPartSearch={setPartSearch}
          />
      )}

      <button className="btn btn-primary" disabled={loading} onClick={handleCreateJob}>
        {loading ? "Creating..." : "Create Job"}
      </button>
    </div>
  );
}

function ProductionSingleJob({
  productionPartList,
  cutSheetList,
  materialList,
  selectedPart,
  setSelectedPart,
  selectedCutSheet,
  setSelectedCutSheet,
  requestedQuantity,
  setRequestedQuantity,
  tempRequestedQuantity,
  setTempRequestedQuantity,
  productionPreviewParts,
  productionSheetsNeeded,
  productionEstimatedLaserHrs,
  filteredProductionPartsSingle,
  partSearch,
  setPartSearch
}) {
  return (
    <div style={{ border: "1px solid #ccc", padding: "1rem", marginBottom: "1rem" }}>
      <h5>Production Job Details</h5>
      <div className="mb-3">
        <label>Primary Part</label>
        <input
          type="text"
          list="datalistSingleParts"
          className="form-select"
          value={partSearch}
          onChange={(e) => {
            setPartSearch(e.target.value);
            const found = filteredProductionPartsSingle.find(
              (p) => p.product_id === e.target.value
            );
            if (found) {
              setSelectedPart(found);
            } else if (selectedPart && selectedPart.product_id !== e.target.value) {
              setSelectedPart(null);
            }
          }}
        />
        <datalist id="datalistSingleParts">
          <option value="">-- Select Part --</option>
          {filteredProductionPartsSingle.map((p) => (
            <option key={p.part_id} value={p.product_id}>
              {p.product_id} - {p.description}
            </option>
          ))}
        </datalist>
        {selectedPart && selectedPart.image_path && (
          <div style={{ marginTop: "0.5rem" }}>
            <img
              src={selectedPart.image_path}
              alt="Part Preview"
              style={{ maxWidth: "300px", border: "1px solid #ccc" }}
            />
          </div>
        )}
      </div>
      <div className="mb-3">
        <label>Cut Sheet</label>
        <select
          className="form-select"
          value={selectedCutSheet?.cut_sheet_id || ""}
          onChange={(e) => {
            const cid = parseInt(e.target.value, 10);
            const foundCS = cutSheetList.find(cs => cs.cut_sheet_id === cid);
            setSelectedCutSheet(foundCS || null);
          }}
        >
          <option value="">-- Select Cut Sheet --</option>
          {cutSheetList
            .filter((cs) => {
              if (!selectedPart) return false;
              if (!cs.parts) return false;
              return cs.parts.some(pr => pr.part_id === selectedPart.part_id);
            })
            .map((cs) => {
              const mat = materialList.find(m => m.material_id === cs.material_id);
              let matInfo = "Unknown Material";
              if (mat) {
                const onHand = mat.stock_quantity; const inbound = mat.inbound_quantity;
                const reserved = mat.reserved_quantity || 0; const available = onHand - reserved;
                const totalAvail = onHand + inbound - reserved;
                matInfo = `${mat.material_code} - ${mat.description}\n` +
                          `On Hand: ${onHand}\n` + `Inbound: ${inbound}\n` +
                          `Avail. On Hand: ${available}\n` + `Total Avail.: ${totalAvail}`;
              }
              let primaryQPS = 0;
              if (cs.parts) {
                const match = cs.parts.find(p => p.part_id === selectedPart?.part_id);
                if (match) primaryQPS = match.quantity_per_sheet;
              }
              
              let labelSuffix = "";
              if (cs.is_current_revision === false) labelSuffix += " [ARCHIVED]";
              if (cs.has_obsolete_parts) labelSuffix += " [NEEDS UPDATE]";
              
              return (
                <option key={cs.cut_sheet_id} value={cs.cut_sheet_id}>
                    {cs.cut_sheet_name} - {cs.description} [ Qty/Sheet: {primaryQPS}, Material {'=>'} 
                    {matInfo.replace(/\n/g, " | ")} ] {labelSuffix}
                </option>
              );
            })}
        </select>
        
        {selectedCutSheet && selectedCutSheet.is_current_revision === false && (
            <div className="alert alert-danger mt-2">
                <strong>Warning:</strong> You have selected an <strong>ARCHIVED</strong> cut sheet. 
                Please verify this is intended, or create a new version of the sheet.
            </div>
        )}
        {selectedCutSheet && selectedCutSheet.has_obsolete_parts && (
            <div className="alert alert-warning mt-2">
                <strong>Warning:</strong> This cut sheet contains <strong>OBSOLETE</strong> parts. 
                It is recommended to update the cut sheet before scheduling.
            </div>
        )}
      </div>
      
      <div className="mb-3">
        <label>Requested Quantity</label>
        <input
          type="number"
          className="form-control"
          value={tempRequestedQuantity}
          onChange={(e) => setTempRequestedQuantity(e.target.value)}
          onBlur={() => {
            if (!selectedPart || !selectedCutSheet) {
              setRequestedQuantity(tempRequestedQuantity); return;
            }
            const rawVal = parseInt(tempRequestedQuantity, 10) || 0;
            let qps = 1;
            if (selectedCutSheet.parts) {
              const match = selectedCutSheet.parts.find(p => p.part_id === selectedPart.part_id);
              if (match) qps = match.quantity_per_sheet;
            }
            const remainder = rawVal % qps;
            let finalQty = rawVal;
            if (remainder !== 0) { finalQty = Math.ceil(rawVal / qps) * qps; }
            setRequestedQuantity(String(finalQty));
            setTempRequestedQuantity(String(finalQty));
          }}
        />
      </div>
      {selectedCutSheet && (
        <div style={{ marginTop: "1rem", padding: "0.5rem", border: "1px solid #ddd", whiteSpace: "pre-line" }}>
          {(() => {
            const mat = materialList.find(m => m.material_id === selectedCutSheet.material_id);
            if (!mat) return <p>Material info not found.</p>;
            const onHand = mat.stock_quantity; const inbound = mat.inbound_quantity;
            const reserved = mat.reserved_quantity || 0; const availableOnHand = onHand - reserved;
            const totalAvail = onHand + inbound - reserved;
            return (
              <div>
                <strong>Material:</strong> {mat.material_code} - {mat.description}{"\n"}
                <strong>On Hand:</strong> {onHand}{"\n"}
                <strong>Inbound:</strong> {inbound}{"\n"}
                <strong>Avail. On Hand:</strong> {availableOnHand}{"\n"}
                <strong>Total Avail.:</strong> {totalAvail}
              </div>
            );
          })()}
          {selectedCutSheet.image_path && (
            <div style={{ marginTop: "0.5rem" }}>
              <img src={selectedCutSheet.image_path} alt="Cut Sheet Preview" style={{ maxWidth: "400px", border: "1px solid #ccc" }} />
            </div>
          )}
        </div>
      )}
      {selectedCutSheet && requestedQuantity && (
        <div style={{ marginTop: "1rem" }}>
          <p><strong>Sheets Needed:</strong> {productionSheetsNeeded}<br /><strong>Estimated Laser Time:</strong> {formatHoursAndMinutes(productionEstimatedLaserHrs)}</p>
          {productionPreviewParts.length > 0 && (
            <table className="table table-sm">
              <thead><tr><th>Part</th><th>Total Qty</th></tr></thead>
              <tbody>{productionPreviewParts.map((pp) => (<tr key={pp.part_id}><td>{pp.product_id} - {pp.description}</td><td>{pp.totalQuantity}</td></tr>))}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function CustomSingleJob({
  jobCategory, materialList, selectedMaterial, setSelectedMaterial, customChosenParts,
  setCustomChosenParts, customSheetsNeeded, setCustomSheetsNeeded, customEstimatedLaserHrs,
  customPreviewParts, customCutSheetImage, setCustomCutSheetImage, customerList,
  customLaserMinutes, setCustomLaserMinutes, useCustomMaterial, setUseCustomMaterial,
  customMaterialDesc, setCustomMaterialDesc
}) {
  const [allParts, setAllParts] = useState([]);
  const [partAddCustomerId, setPartAddCustomerId] = useState(null);
  const [partAddPartId, setPartAddPartId] = useState(null);
  const [materialSource, setMaterialSource] = useState("stock"); // "stock" | "remnant" | "custom"
  const [remnantList, setRemnantList] = useState([]);
  const [remnantLoading, setRemnantLoading] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [thicknessFilter, setThicknessFilter] = useState("");
  const [finishFilter, setFinishFilter] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/parts`).then(res => res.json()).then(setAllParts).catch(err => console.error("Failed to load all parts", err));
    // Load remnants for the remnant picker
    fetch(`${API_BASE}/materials/remnants`).then(res => res.json()).then(setRemnantList).catch(() => {});
  }, []);

  // Sync materialSource with existing useCustomMaterial prop
  useEffect(() => {
    if (materialSource === "custom") {
      setUseCustomMaterial(true);
      setSelectedMaterial(null);
    } else {
      setUseCustomMaterial(false);
    }
  }, [materialSource]);

  const filteredPartsForCustomer = useMemo(() => {
    if (!partAddCustomerId) return [];
    return allParts.filter(p => p.customer_id === parseInt(partAddCustomerId, 10));
  }, [partAddCustomerId, allParts]);

  const handleAddPartToSheet = () => {
    const qStr = prompt(`How many of this part per sheet?`, "1");
    if (!qStr) return;
    const qNum = parseInt(qStr, 10);
    if (isNaN(qNum) || qNum < 1) return;
    const customer = customerList.find(c => c.customer_id === parseInt(partAddCustomerId, 10));
    const part = allParts.find(p => p.part_id === parseInt(partAddPartId, 10));
    if (customer && part) {
      setCustomChosenParts(prev => [...prev, { part, customer, quantity: qNum }]);
    }
    setPartAddCustomerId(null);
    setPartAddPartId(null);
  };

  function checkAndSetFile(file) {
    const img = new Image();
    img.onload = () => {
      if (img.width > 1920 || img.height > 1080) {
        alert("Image is larger than 1920x1080. Please resize or pick a smaller screenshot."); return;
      }
      setCustomCutSheetImage(file);
    };
    img.src = URL.createObjectURL(file);
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file) checkAndSetFile(file);
  }

  function handlePaste(e) {
    if (e.clipboardData?.files?.length) {
      const file = e.clipboardData.files[0];
      checkAndSetFile(file);
      e.preventDefault();
    }
  }

  const filterOptions = useMemo(() => {
    const owners = new Map(), types = new Set(), thicknesses = new Set(), finishes = new Set();
    const customerMap = new Map(customerList.map(c => [c.customer_id, c.name]));
    materialList.forEach(m => {
        if (m.material_category === jobCategory) {
            if (m.owner_customer_id && !owners.has(m.owner_customer_id)) owners.set(m.owner_customer_id, customerMap.get(m.owner_customer_id) || `ID: ${m.owner_customer_id}`);
            if (m.material_type) types.add(m.material_type);
            if (m.thickness) thicknesses.add(m.thickness);
            if (m.surface_finish) finishes.add(m.surface_finish);
        }
    });
    return {
        owners: Array.from(owners.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
        types: [...types].sort(), thicknesses: [...thicknesses].sort((a, b) => a.localeCompare(b, undefined, {numeric: true})), finishes: [...finishes].sort()
    };
  }, [materialList, customerList, jobCategory]);

  const filteredMaterials = useMemo(() => {
    return materialList.filter(m => {
        if (m.material_category !== jobCategory) return false;
        if (ownerFilter && m.owner_customer_id !== parseInt(ownerFilter, 10)) return false;
        if (typeFilter && m.material_type !== typeFilter) return false;
        if (thicknessFilter && m.thickness !== thicknessFilter) return false;
        if (finishFilter && m.surface_finish !== finishFilter) return false;
        return true;
    });
  }, [materialList, jobCategory, ownerFilter, typeFilter, thicknessFilter, finishFilter]);

  return (
    <div style={{ border: "1px solid #ccc", padding: "1rem", marginBottom: "1rem" }} onPaste={handlePaste}>
      <h5>Custom Job Details</h5>
      <div className="mb-3">
        <label className="form-label fw-bold">Material Source</label>
        <div className="btn-group w-100" role="group">
          <input type="radio" className="btn-check" name="materialSource" id="srcStock" value="stock" checked={materialSource === "stock"} onChange={e => { setMaterialSource(e.target.value); setSelectedMaterial(null); }} />
          <label className="btn btn-outline-secondary" htmlFor="srcStock">Stock Material</label>
          <input type="radio" className="btn-check" name="materialSource" id="srcRemnant" value="remnant" checked={materialSource === "remnant"} onChange={e => { setMaterialSource(e.target.value); setSelectedMaterial(null); }} />
          <label className="btn btn-outline-secondary" htmlFor="srcRemnant">Remnant</label>
          <input type="radio" className="btn-check" name="materialSource" id="srcCustom" value="custom" checked={materialSource === "custom"} onChange={e => { setMaterialSource(e.target.value); setSelectedMaterial(null); }} />
          <label className="btn btn-outline-secondary" htmlFor="srcCustom">Non-Inventory</label>
        </div>
      </div>
      {materialSource === "custom" ? (
        <div className="mb-3"><label>Describe the Material (e.g., size, type, owner)</label><input type="text" className="form-control" value={customMaterialDesc} onChange={(e) => setCustomMaterialDesc(e.target.value)} placeholder="e.g., Customer-supplied 2'x3' 1/8in acrylic" /></div>
      ) : materialSource === "remnant" ? (
        <div className="mb-3"><label>Remnant (from Inventory)</label>
          <select className="form-select" value={selectedMaterial?.material_id || ""} onChange={(e) => { const mid = parseInt(e.target.value, 10); const foundMat = remnantList.find(r => r.material_id === mid); setSelectedMaterial(foundMat || null); }}>
            <option value="">-- Select Remnant --</option>
            {remnantList.filter(r => r.available_quantity > 0).map(r => (
              <option key={r.material_id} value={r.material_id}>
                {r.material_code} ({r.width}"x{r.length}") [{r.display_thickness} {r.surface_finish}] Qty: {r.stock_quantity}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div className="p-2 border rounded mb-3 bg-light">
            <label className="form-label fw-bold">Filter Materials</label>
            <div className="row g-2">
              <div className="col-md-3"><select className="form-select form-select-sm" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}><option value="">All Owners</option>{filterOptions.owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
              <div className="col-md-3"><select className="form-select form-select-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}><option value="">All Types</option>{filterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
              <div className="col-md-3"><select className="form-select form-select-sm" value={thicknessFilter} onChange={e => setThicknessFilter(e.target.value)}><option value="">All Thicknesses</option>{filterOptions.thicknesses.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
              <div className="col-md-3"><select className="form-select form-select-sm" value={finishFilter} onChange={e => setFinishFilter(e.target.value)}><option value="">All Finishes</option>{filterOptions.finishes.map(f => <option key={f} value={f}>{f}</option>)}</select></div>
            </div>
          </div>
          <div className="mb-3"><label>Material (from Inventory)</label>
            <select className="form-select" value={selectedMaterial?.material_id || ""} onChange={(e) => { const mid = parseInt(e.target.value, 10); const foundMat = materialList.find(m => m.material_id === mid); setSelectedMaterial(foundMat || null); }}>
              <option value="">-- Select Material --</option>
              {filteredMaterials.map(m => { const onHand = m.stock_quantity, inbound = m.inbound_quantity, reserved = m.reserved_quantity || 0, totalAvail = onHand + inbound - reserved;
                return (<option key={m.material_id} value={m.material_id}>{m.material_code} - {m.description} [On-Hand: {onHand}, Inbound: {inbound}, Total: {totalAvail}]</option>);
              })}
            </select>
          </div>
        </>
      )}
      <div className="mb-3"><label>Sheets To Cut</label><input type="number" className="form-control" min={1} value={customSheetsNeeded} onChange={(e) => { const val = parseInt(e.target.value, 10); setCustomSheetsNeeded(val > 0 ? val : 1); }} /></div>
      <div className="mb-3"><label>Estimated Laser Time (minutes per sheet)</label><input type="number" className="form-control" value={customLaserMinutes} onChange={(e) => setCustomLaserMinutes(e.target.value)} placeholder="e.g., 15" /><small className="form-text text-muted">Provide an estimate for one-off or custom laser jobs.</small></div>
      <div className="mb-3"><label className="form-label fw-bold">Add Parts to Sheet</label>
        <div className="p-2 border rounded">
          <div className="row g-2 align-items-end">
            <div className="col-md-4"><label className="form-label-sm">1. Select Customer</label><select className="form-select form-select-sm" value={partAddCustomerId || ""} onChange={e => { setPartAddCustomerId(e.target.value); setPartAddPartId(null); }}><option value="">-- Customer --</option>{customerList.map(c => (<option key={c.customer_id} value={c.customer_id}>{c.name}</option>))}</select></div>
            <div className="col-md-5"><label className="form-label-sm">2. Select Part</label><select className="form-select form-select-sm" value={partAddPartId || ""} onChange={e => setPartAddPartId(e.target.value)} disabled={!partAddCustomerId}><option value="">-- Part --</option>{filteredPartsForCustomer.map(p => (<option key={p.part_id} value={p.part_id}>{p.product_id} - {p.description}</option>))}</select></div>
            <div className="col-md-3"><button className="btn btn-sm btn-secondary w-100" disabled={!partAddPartId} onClick={handleAddPartToSheet}>+ Add Part</button></div>
          </div>
        </div>
      </div>
      <div className="mt-3"><strong>Parts on this Cut Sheet:</strong>{customChosenParts.length === 0 ? <p className="text-muted small mt-1">No parts added yet.</p> : (<ul className="list-group mt-2">{customChosenParts.map((cp, idx) => (<li key={idx} className="list-group-item d-flex justify-content-between align-items-center"><div><strong>{cp.customer.name}</strong> - {cp.part.product_id}<br /><small className="text-muted">{cp.part.description}</small></div><div><span className="badge bg-primary rounded-pill me-3">Qty/Sheet: {cp.quantity}</span><button type="button" className="btn-close" aria-label="Remove" onClick={() => setCustomChosenParts(prev => prev.filter((_, i) => i !== idx))}></button></div></li>))}</ul>)}</div>
      {customChosenParts.length > 0 && (
        <div style={{ marginTop: "1rem" }}><p><strong>Sheets To Cut:</strong> {customSheetsNeeded}<br /><strong>Estimated Laser Time:</strong> {formatHoursAndMinutes(customEstimatedLaserHrs)}</p>
          {customPreviewParts.length > 0 && (
            <table className="table table-sm"><thead><tr><th>Customer</th><th>Part</th><th>Qty per Sheet</th><th>Total Qty</th></tr></thead>
              <tbody>{customPreviewParts.map((cp, idx) => (<tr key={idx}><td>{cp.customer.name}</td><td>{cp.part.product_id} - {cp.part.description}</td><td>{cp.quantity}</td><td>{cp.totalQuantity}</td></tr>))}</tbody>
            </table>
          )}
        </div>
      )}
      <div className="mb-3" style={{ marginTop: "1rem" }}><label>Custom Cut Sheet Image (Optional)</label><input type="file" accept="image/*" onChange={handleFileChange} style={{ display: "block", margin: "0.5rem 0" }} />{customCutSheetImage && <div><p>Preview:</p><img src={URL.createObjectURL(customCutSheetImage)} alt="Custom Cut Sheet" style={{ maxWidth: "400px", border: "1px solid #ccc" }} /></div>}<p style={{ fontSize: "0.9rem", color: "#666" }}>You can also paste an image (Ctrl+V) directly here.</p></div>
    </div>
  );
}

function ShapeStockSingleJob({
  productionPartList, cutListList, materialList, selectedPart, setSelectedPart,
  selectedCutList, setSelectedCutList, shapeRequestedQuantity, setShapeRequestedQuantity,
  tempShapeRequestedQuantity, setTempShapeRequestedQuantity, shapeStockPreviewParts, sticksNeeded,
  filteredProductionPartsSingle, partSearch, setPartSearch
}) {
  return (
    <div style={{ border: "1px solid #ccc", padding: "1rem", marginBottom: "1rem" }}>
      <h5>Shape Stock Job Details</h5>
      <div className="mb-3"><label>Primary Part</label><input type="text" list="datalistShapeStockParts" className="form-select" value={partSearch} onChange={(e) => { setPartSearch(e.target.value); const found = filteredProductionPartsSingle.find((p) => p.product_id === e.target.value); setSelectedPart(found || null); }} /><datalist id="datalistShapeStockParts">{filteredProductionPartsSingle.map((p) => (<option key={p.part_id} value={p.product_id}>{p.product_id} - {p.description}</option>))}</datalist></div>
      <div className="mb-3"><label>Cut List</label>
        <select className="form-select" value={selectedCutList?.cut_list_id || ""} onChange={(e) => { const cid = parseInt(e.target.value, 10); const foundCL = cutListList.find(cl => cl.cut_list_id === cid); setSelectedCutList(foundCL || null); }} disabled={!selectedPart}>
          <option value="">-- Select Cut List --</option>
          {cutListList.filter((cl) => cl.parts?.some(p => p.part_id === selectedPart?.part_id)).map((cl) => {
            const mat = materialList.find(m => m.material_id === cl.material_id);
            const matInfo = mat ? `${mat.material_code} - ${mat.description}` : 'Unknown Material';
            return (<option key={cl.cut_list_id} value={cl.cut_list_id}>{cl.cut_list_name} - {cl.description} [Material: {matInfo}]</option>);
          })}
        </select>
      </div>
      <div className="mb-3"><label>Requested Quantity</label><input type="number" className="form-control" value={tempShapeRequestedQuantity} onChange={(e) => setTempShapeRequestedQuantity(e.target.value)} onBlur={() => {
        if (!selectedPart || !selectedCutList) { setShapeRequestedQuantity(tempShapeRequestedQuantity); return; }
        const rawVal = parseInt(tempShapeRequestedQuantity, 10) || 0;
        let qpl = 1;
        if (selectedCutList.parts) { const match = selectedCutList.parts.find(p => p.part_id === selectedPart.part_id); if (match && match.quantity_per_length > 0) qpl = match.quantity_per_length; }
        const remainder = rawVal % qpl; let finalQty = rawVal;
        if (remainder !== 0) { finalQty = Math.ceil(rawVal / qpl) * qpl; }
        setShapeRequestedQuantity(String(finalQty)); setTempShapeRequestedQuantity(String(finalQty));
      }} disabled={!selectedCutList} /></div>
      {selectedCutList && shapeRequestedQuantity && shapeStockPreviewParts.length > 0 && (
        <div style={{ marginTop: "1rem" }}><p><strong>Sticks Needed:</strong> {sticksNeeded}</p><p><strong>Total Parts Produced:</strong></p>
          <table className="table table-sm"><thead><tr><th>Part</th><th>Total Qty</th></tr></thead>
            <tbody>{shapeStockPreviewParts.map((p) => (<tr key={p.part_id}><td>{p.product_id} - {p.description}</td><td>{Math.round(p.totalQuantity)}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BulkJobCreator({
  entryMode,
  // jobCategory, // <--- IGNORED in Bulk mode now!
  bulkRows, setBulkRows, bulkCustomerId, setBulkCustomerId, bulkPoNumber,
  setBulkPoNumber, bulkSoNumber, setBulkSoNumber, bulkShippingMethod, setBulkShippingMethod,
  handleAddBulkRow, handleDuplicateLastRow, handleCreateBulkJobs, materialList,
  productionPartList, cutSheetList, cutListList, customerList, loading, error,
  // filteredProductionPartsBulk, // <--- Using full list instead to allow mixed types
  handleBulkRowChange, handleRemoveBulkRow,
  poFile, setPoFile, customerPO,
  onOpenCustomModal,
  usersList = [],
  partInProductionMap = new Map(),
  orderPartIds = new Set(),
  createBlockedReason = null
}) {
  const [duplicateCount, setDuplicateCount] = useState(1);

  // A non-custom row needs a part + a matching cut source + a quantity before it
  // can create a job with tasks. Block "Create All Jobs" until every row is
  // complete so a missing cut sheet can't create an empty, taskless job. (Custom
  // rows carry their own generated cut sheet.)
  const rowsIncomplete = bulkRows.some(r => {
      if (r.isCustom) return false;
      const rt = r.part?.stock_type || 'Sheet Stock';
      const hasSource = rt === 'Shape Stock' ? !!r.cutList : !!r.cutSheet;
      return !r.part || !hasSource || !r.quantity;
  });

  function handleDuplicateMultiple() {
    if (bulkRows.length === 0 || duplicateCount < 1) return;
    const lastRow = bulkRows[bulkRows.length - 1]; const newRows = [];
    for (let i = 0; i < duplicateCount; i++) { newRows.push({ ...lastRow, id: Date.now() + i, error: null }); }
    setBulkRows(prev => [...prev, ...newRows]);
  }
  
  // Calculate Material Usage (Requires looking at individual row type now)
  const usageByMaterial = {};
  for (const row of bulkRows) { 
      const rowType = row.part?.stock_type || 'Sheet Stock';
      const source = rowType === 'Sheet Stock' ? row.cutSheet : row.cutList; 
      if (source?.material_id) { 
          const mid = source.material_id; 
          const needed = rowType === 'Sheet Stock' ? row.sheetsNeeded : row.sticksNeeded; 
          usageByMaterial[mid] = (usageByMaterial[mid] || 0) + needed; 
      } 
  }
  
  const lockedStyle = { backgroundColor: '#e9ecef', cursor: 'not-allowed' };
  const isSalesOrderMode = entryMode === 'sales_order';

  return (
    <div style={{ border: "1px solid #ccc", padding: "1rem", margin: "0 auto" }}>
      <h4>Bulk Job Creation</h4>
      {isSalesOrderMode && <p className="text-primary small mb-2"><i className="bi bi-lock-fill"></i> Customer & PO are locked to the selected Sales Order.</p>}
      
      <div className="mb-3">
        <label>Customer</label>
        <select 
            className="form-select" 
            value={bulkCustomerId || ""} 
            onChange={(e) => setBulkCustomerId(e.target.value)}
            disabled={isSalesOrderMode}
            style={isSalesOrderMode ? lockedStyle : {}}
        >
            <option value="">-- Select Customer --</option>
            {customerList.map((c) => (<option key={c.customer_id} value={c.customer_id}>{c.name}</option>))}
        </select>
      </div>
      
      <div className="mb-3">
        <label>Customer PO (Text)</label>
        <input
            type="text"
            className="form-control"
            value={bulkPoNumber}
            onChange={(e) => setBulkPoNumber(e.target.value)}
            disabled={isSalesOrderMode}
            style={isSalesOrderMode ? lockedStyle : {}}
            placeholder={isSalesOrderMode ? "Linked from Sales Order" : ""}
        />
        <label className="mt-2">Cust SO</label>
        <input
            type="text"
            className="form-control"
            value={bulkSoNumber}
            onChange={(e) => setBulkSoNumber(e.target.value)}
            disabled={isSalesOrderMode}
            style={isSalesOrderMode ? lockedStyle : {}}
            placeholder={isSalesOrderMode ? "Linked from Sales Order" : ""}
        />
      </div>
      
      {!customerPO && (
        <div className="mb-3">
            <label>Upload Customer PO (PDF/Image)</label>
            <input 
                type="file" 
                className="form-control" 
                accept="application/pdf,image/*" 
                onChange={(e) => setPoFile(e.target.files[0])} 
            />
            <small className="text-muted">This file will be linked to ALL jobs created in this bulk batch.</small>
        </div>
      )}
      {customerPO && (<div className="mb-3 alert alert-info"><strong>Linked PO:</strong> {customerPO.original_filename}</div>)}

      <div className="mb-3"><label>Shipping Method</label><select className="form-select" value={bulkShippingMethod} onChange={(e) => setBulkShippingMethod(e.target.value)}><option value="Customer Pickup">Customer Pickup</option><option value="Delivery">Delivery</option><option value="Freight">Freight</option><option value="Ground Shipping">Ground Shipping</option></select></div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light small"><tr>
            <th style={{ minWidth: 220 }}>Primary Part</th>
            <th style={{ minWidth: 260 }}>Source (Cut Sheet / List)</th>
            <th style={{ minWidth: 90 }}>Quantity</th>
            <th style={{ minWidth: 150 }}>Priority</th>
            <th style={{ minWidth: 150 }}>Due Date</th>
            <th style={{ minWidth: 130 }}>Auth User</th>
            <th style={{ minWidth: 160 }}>Job Notes</th>
            <th style={{ minWidth: 170 }}>Material Info</th>
            <th style={{ minWidth: 90 }}>Material Units</th>
            <th style={{ minWidth: 80 }}>Est Laser Hrs</th>
            <th style={{ minWidth: 210 }}>Parts Preview</th>
            <th>Error</th>
            <th>Delete</th>
          </tr></thead>
          <tbody>{bulkRows.map((row) => {
              // 1. Determine Type per Row
              const rowType = row.part?.stock_type || 'Sheet Stock';
              
              // 2. Filter Sources based on type
              const relevantSheets = (rowType === 'Sheet Stock' && row.part) ? cutSheetList.filter(cs => cs.parts?.some(pr => pr.part_id === row.part.part_id)) : [];
              const relevantLists = (rowType === 'Shape Stock' && row.part) ? cutListList.filter(cl => cl.parts?.some(pr => pr.part_id === row.part.part_id)) : [];
              
              // 3. Get Material Info
              let matInfo = "";
              let matStock = null;
              let matLabel = "";
              let matTitle = "";
              const source = rowType === 'Sheet Stock' ? row.cutSheet : row.cutList;
              if (source?.material_id) {
                  const matId = source.material_id;
                  const mat = materialList.find(m => m.material_id === matId);
                  if (mat) {
                      const used = usageByMaterial[matId] || 0;
                      const onHand = mat.stock_quantity;
                      const inbound = mat.inbound_quantity;
                      const reserved = mat.reserved_quantity || 0;
                      const availableOnHand = onHand - reserved - used;
                      const totalAvail = (onHand - reserved) + inbound - used;
                      matStock = { onHand, inbound, availableOnHand, totalAvail };
                      matLabel = mat.description || mat.material_code || "";
                      matTitle = [mat.material_code, mat.description].filter(Boolean).join(' - ');
                  }
              } else if (row.isCustom && row.customMaterialDesc) {
                  matInfo = row.customMaterialDesc;
              }
              // What the chosen source makes per sheet / stick, and the OTHER parts it carries (a shared sheet).
              const sourceParts = (source && !row.isCustom) ? (source.parts || []) : [];
              const perUnit = row.part ? (sourceParts.find(p => p.part_id === row.part.part_id) || null) : null;
              const perUnitQty = perUnit ? (rowType === 'Shape Stock' ? perUnit.quantity_per_length : perUnit.quantity_per_sheet) : null;
              const otherSourceParts = row.part ? sourceParts.filter(p => p.part_id !== row.part.part_id) : [];
              const unitWord = rowType === 'Shape Stock' ? 'stick' : 'sheet';

              return (
                <tr key={row.id} style={row.isCustom ? { backgroundColor: '#fff8e1' } : {}}>
                  <td>
                      {row.isCustom ? (
                          <span className="form-control" style={{ backgroundColor: '#e9ecef', cursor: 'default' }}>{row.partSearch}</span>
                      ) : (
                      <>
                      <input type="text" list="datalistBulkParts" className="form-select" value={row.partSearch}
                        onChange={(e) => {
                            const sku = e.target.value.trim();
                            handleBulkRowChange(row.id, "partSearch", sku);
                            // Search in full list now, not filtered by global category
                            const foundPart = productionPartList.find(p => p.product_id === sku && (!bulkCustomerId || p.customer_id === parseInt(bulkCustomerId, 10)));
                            const resetFields = { part: foundPart || null, cutSheet: null, cutList: null, quantity: 0, tempQuantity: "", tempMaterialUnits: undefined, previewParts: [], sheetsNeeded: 0, sticksNeeded: 0, laserHrs: 0, error: null };
                            setBulkRows(prev => prev.map(r => r.id === row.id ? { ...r, ...resetFields } : r));
                        }}
                      />
                      <datalist id="datalistBulkParts">
                          <option value="">--Select Part--</option>
                          {/* Allow both types in the dropdown */}
                          {productionPartList.filter(p => !bulkCustomerId || p.customer_id === parseInt(bulkCustomerId, 10)).map((p) => (
                              <option key={p.part_id} value={p.product_id}>{p.product_id} - {p.description} ({p.stock_type || "Sheet"})</option>
                          ))}
                      </datalist>
                      </>
                      )}
                      {row.part && (
                          <div className="small text-muted mt-1" style={{ lineHeight: 1.2 }} title={`${row.part.product_id} - ${row.part.description || ''}`} data-testid="bulk-part-caption">
                              {row.part.description || ''}
                              <span className="ms-1 badge text-bg-light border">{rowType === 'Shape Stock' ? 'Shape' : 'Sheet'}</span>
                          </div>
                      )}
                  </td>
                  <td>
                      {row.isCustom ? (
                          <div><span className="badge bg-warning text-dark me-1">Custom</span><small className="text-muted">{row.customMaterialDesc || ''}</small></div>
                      ) : rowType === 'Sheet Stock' ? (
                          <select className="form-select" title={row.cutSheet?.cut_sheet_name || ''} value={row.cutSheet?.cut_sheet_id || ""} onChange={(e) => { const cid = parseInt(e.target.value, 10), cfound = relevantSheets.find(cs => cs.cut_sheet_id === cid); handleBulkRowChange(row.id, "cutSheet", cfound || null); }}>
                              <option value="">--Select Sheet--</option>
                              {relevantSheets.map(cs => {
                                  let label = cs.cut_sheet_name;
                                  if (cs.is_current_revision === false) label += " [ARCHIVED]";
                                  if (cs.has_obsolete_parts) label += " [NEEDS UPDATE]";
                                  return (<option key={cs.cut_sheet_id} value={cs.cut_sheet_id}>{label}</option>);
                              })}
                          </select>
                      ) : (
                          <select className="form-select" title={row.cutList?.cut_list_name || ''} value={row.cutList?.cut_list_id || ""} onChange={(e) => { const cid = parseInt(e.target.value, 10), cfound = relevantLists.find(cl => cl.cut_list_id === cid); handleBulkRowChange(row.id, "cutList", cfound || null); }}>
                              <option value="">--Select List--</option>
                              {relevantLists.map(cl => (<option key={cl.cut_list_id} value={cl.cut_list_id}>{cl.cut_list_name}</option>))}
                          </select>
                      )}
                      {source && !row.isCustom && (
                          <div className="small text-muted mt-1" style={{ lineHeight: 1.2 }} data-testid="bulk-source-caption">
                              {matLabel && <div title={matTitle} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{matLabel}</div>}
                              {perUnitQty ? <div>{perUnitQty} per {unitWord}</div> : null}
                              {otherSourceParts.length > 0 && (
                                  <div className="text-warning-emphasis" title={otherSourceParts.map(p => `${p.product_id} x${rowType === 'Shape Stock' ? p.quantity_per_length : p.quantity_per_sheet}`).join(', ')}>
                                      <i className="bi bi-layers me-1"></i>Shared {unitWord}: +{otherSourceParts.length} other part{otherSourceParts.length === 1 ? '' : 's'}
                                  </div>
                              )}
                          </div>
                      )}
                  </td>
                  <td>
                      <input type="number" className="form-control" value={row.tempQuantity} onChange={(e) => handleBulkRowChange(row.id, "tempQuantity", e.target.value)}
                          onBlur={() => {
                              setBulkRows(prev => prev.map(r => {
                                  if (r.id !== row.id) return r;
                                  const rType = r.part?.stock_type || 'Sheet Stock';
                                  const raw = parseInt(r.tempQuantity, 10) || 0;
                                  if (rType === 'Sheet Stock' && r.cutSheet) {
                                      let qps = 1;
                                      const match = r.cutSheet.parts?.find(p => p.part_id === r.part?.part_id);
                                      if (match) qps = match.quantity_per_sheet;
                                      const finalQty = (raw % qps !== 0) ? Math.ceil(raw / qps) * qps : raw;
                                      return { ...r, quantity: finalQty, tempQuantity: String(finalQty) };
                                  } else if (rType === 'Shape Stock' && r.cutList) {
                                      let qpl = 1;
                                      const match = r.cutList.parts?.find(p => p.part_id === r.part?.part_id);
                                      if (match) qpl = match.quantity_per_length;
                                      const finalQty = (raw % qpl !== 0) ? Math.ceil(raw / qpl) * qpl : raw;
                                      return { ...r, quantity: finalQty, tempQuantity: String(finalQty) };
                                  }
                                  return { ...r, quantity: raw, tempQuantity: String(raw) };
                              }));
                          }}
                      />
                      {row.assemblyHint && (() => {
                          const hint = row.assemblyHint;
                          const expected = hint.expected_jp_quantity || 0;
                          const inProd = partInProductionMap.get(row.part?.part_id) || 0;
                          // Qty already staged for this same part on OTHER rows of this batch.
                          const stagedElse = bulkRows.reduce((s, r) => (
                              r.id !== row.id && r.part?.part_id && r.part.part_id === row.part?.part_id
                                  ? s + (parseInt(r.tempQuantity, 10) || 0)
                                  : s
                          ), 0);
                          const current = parseInt(row.tempQuantity, 10) || 0;
                          const remaining = Math.max(0, expected - inProd - stagedElse);

                          // The server guard is per-job: it rejects (needs override)
                          // only when THIS job's qty exceeds the SO-wide expected.
                          // Mirror it exactly so the badge predicts the 400 and never
                          // fires on a legitimate partial top-up of the remaining qty.
                          const willReject = current > 0 && current > expected;
                          const onTarget = current > 0 && current === remaining;

                          const breakdown = hint.assembly_breakdown;
                          const derivation = (breakdown && breakdown.length)
                              ? breakdown.map(b => b.is_primary
                                  ? `${b.assembly_name}: ${b.assemblies_ordered}× primary = ${b.subtotal}`
                                  : `${b.assembly_name}: ${b.assemblies_ordered}× × qpa=${b.qty_per_assembly} = ${b.subtotal}`
                                ).join('  +  ')
                              : (hint.is_primary
                                  ? `${hint.assembly_name}: ${hint.assemblies_ordered}× primary`
                                  : `${hint.assembly_name}: ${hint.assemblies_ordered}× × qpa=${hint.qty_per_assembly}`);
                          const tooltip =
                              `Expected ${expected} total` +
                              (breakdown && breakdown.length > 1 ? ` (shared across ${breakdown.length} assemblies)` : '') +
                              ` = ${derivation}.` +
                              ` In production: ${inProd}.` +
                              (stagedElse ? ` Staged on other rows: ${stagedElse}.` : '') +
                              ` Remaining to stage: ${remaining}.`;

                          return (
                              <div className="small mt-1" style={{lineHeight: '1.15'}}>
                                  <span className="text-muted">Remaining: </span>
                                  <strong className={willReject ? 'text-danger' : (onTarget ? 'text-success' : 'text-muted')} title={tooltip}>
                                      {remaining}
                                  </strong>
                                  <span className="text-muted ms-1" title={tooltip}>
                                      (of {expected}{inProd ? `, ${inProd} in prod` : ''}{breakdown && breakdown.length > 1 ? `, ${breakdown.length} assemblies` : ''})
                                  </span>
                                  {willReject && (
                                      <span className="badge bg-warning text-dark ms-1" title="Exceeds the sales order's total assembly demand. The API will reject with 400 unless force_assembly_qty_override is set.">
                                          ⚠ override
                                      </span>
                                  )}
                              </div>
                          );
                      })()}
                  </td>
                  <td><select className="form-select" value={row.jobPriority} onChange={(e) => handleBulkRowChange(row.id, "jobPriority", parseInt(e.target.value, 10))}><option value={1}>1 (Critical)</option><option value={2}>2 (High)</option><option value={3}>3 (Normal)</option><option value={4}>4 (Low)</option></select></td>
                  <td><input type="date" className="form-control" value={row.dueDate} onChange={(e) => setBulkRows(prev => prev.map(r => r.id === row.id ? { ...r, dueDate: e.target.value, dueDateDirty: true } : r))} /></td>
                  <td>
                      <select
                          className="form-select form-select-sm"
                          value={row.authorizedUserId || ""}
                          onChange={(e) => handleBulkRowChange(row.id, "authorizedUserId", e.target.value)}
                      >
                          <option value="">All Users</option>
                          {usersList.map(u => (
                              <option key={u.user_id} value={u.user_id}>{u.name}</option>
                          ))}
                      </select>
                  </td>
                  <td>
                      <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={row.notes || ""}
                          onChange={(e) => handleBulkRowChange(row.id, "notes", e.target.value)}
                          placeholder="Optional notes…"
                      />
                  </td>
                  <td className="small" data-testid="bulk-material-info">
                      {matStock ? (
                          <div style={{ lineHeight: 1.35 }}>
                              <div>On hand <strong>{matStock.onHand}</strong> · Inbound <strong>{matStock.inbound}</strong></div>
                              <div className={matStock.availableOnHand < 0 ? 'text-danger fw-semibold' : ''}>Avail. on hand <strong>{matStock.availableOnHand}</strong></div>
                              <div className={matStock.totalAvail < 0 ? 'text-danger fw-semibold' : ''}>Total avail. <strong>{matStock.totalAvail}</strong></div>
                          </div>
                      ) : (matInfo ? <span style={{ whiteSpace: 'pre-line' }}>{matInfo}</span> : null)}
                  </td>
                  <td>
                      <input
                          type="number"
                          className="form-control"
                          min={0}
                          value={row.tempMaterialUnits !== undefined && row.tempMaterialUnits !== null
                              ? row.tempMaterialUnits
                              : String(rowType === 'Shape Stock' ? (row.sticksNeeded || 0) : (row.sheetsNeeded || 0))}
                          onChange={(e) => handleBulkRowChange(row.id, "tempMaterialUnits", e.target.value)}
                          onBlur={() => {
                              setBulkRows(prev => prev.map(r => {
                                  if (r.id !== row.id) return r;
                                  const rType = r.part?.stock_type || 'Sheet Stock';
                                  const units = parseInt(r.tempMaterialUnits, 10);
                                  if (isNaN(units) || units < 0) {
                                      return { ...r, tempMaterialUnits: undefined };
                                  }
                                  if (rType === 'Sheet Stock' && r.cutSheet) {
                                      let qps = 1;
                                      const match = r.cutSheet.parts?.find(p => p.part_id === r.part?.part_id);
                                      if (match) qps = match.quantity_per_sheet;
                                      const newQty = units * qps;
                                      return { ...r, quantity: newQty, tempQuantity: String(newQty), tempMaterialUnits: undefined };
                                  } else if (rType === 'Shape Stock' && r.cutList) {
                                      let qpl = 1;
                                      const match = r.cutList.parts?.find(p => p.part_id === r.part?.part_id);
                                      if (match) qpl = match.quantity_per_length;
                                      const newQty = units * qpl;
                                      return { ...r, quantity: newQty, tempQuantity: String(newQty), tempMaterialUnits: undefined };
                                  }
                                  return { ...r, tempMaterialUnits: undefined };
                              }));
                          }}
                          disabled={!row.part || (rowType === 'Sheet Stock' ? !row.cutSheet : !row.cutList)}
                      />
                      <div className="small text-muted mt-1">{unitWord}s</div>
                  </td>
                  <td>{rowType === 'Shape Stock' ? 'N/A' : formatHoursAndMinutes(row.laserHrs)}</td>
                  <td className="small">{row.previewParts?.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: "1rem" }} data-testid="bulk-parts-preview">
                          {row.previewParts.map((p) => {
                              const isPrimary = row.part && p.part_id === row.part.part_id;
                              const otherOrder = orderPartIds.size > 0 && !orderPartIds.has(p.part_id);
                              const badIdea = otherOrder && isBadIdeaCustomer(
                                  productionPartList?.find(pp => pp.part_id === p.part_id)?.customer_id ?? p.customer_id);
                              return (
                                  <li key={p.part_id} className={isPrimary ? 'fw-semibold' : ''}>
                                      {p.product_id}: {p.totalQuantity}
                                      {badIdea && (
                                          <span className="badge text-bg-info ms-1" title="A Bad Idea Co. part: it auto-ships to Bad Idea stock (no allocation needed)">Bad Idea stock</span>
                                      )}
                                      {otherOrder && !badIdea && (
                                          <span className="badge text-bg-warning ms-1" title="Not a line of this sales order: allocate it to its own order (Other Parts Being Produced below)">other order</span>
                                      )}
                                  </li>
                              );
                          })}
                      </ul>
                  )}</td>
                  <td style={{ color: "red" }}>{row.error || ""}</td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => handleRemoveBulkRow(row.id)}>Delete</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex gap-2 align-items-center flex-wrap"><button className="btn btn-secondary" onClick={handleAddBulkRow}>+ Add Row</button><button className="btn btn-info" onClick={handleDuplicateLastRow} disabled={bulkRows.length === 0}>Duplicate Last Row</button><div className="d-flex align-items-center gap-2" style={{borderLeft: '2px solid #ccc', paddingLeft: '0.75rem'}}><input type="number" className="form-control form-control-sm" value={duplicateCount} onChange={(e) => setDuplicateCount(Math.max(1, parseInt(e.target.value) || 1))} style={{ width: '70px' }} aria-label="Number of duplicates" /><button className="btn btn-outline-info btn-sm" onClick={handleDuplicateMultiple} disabled={bulkRows.length === 0}>Duplicate Multiple</button></div>{onOpenCustomModal && <div className="d-flex align-items-center" style={{borderLeft: '2px solid #ccc', paddingLeft: '0.75rem'}}><button className="btn btn-outline-warning" onClick={onOpenCustomModal}><i className="bi bi-file-earmark-plus me-1"></i>Add Custom Cut Sheet</button></div>}</div>
      <BulkPartsSummary bulkRows={bulkRows} /><hr />
      <button
        className="btn btn-primary"
        onClick={handleCreateBulkJobs}
        disabled={loading || bulkRows.length === 0 || rowsIncomplete || !!createBlockedReason}
        title={createBlockedReason || (rowsIncomplete ? "Every row needs a part, a cut sheet/list, and a quantity before jobs can be created." : undefined)}
      >{loading ? "Creating..." : "Create All Jobs"}</button>
      {rowsIncomplete && (
        <div className="text-danger small mt-1">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Each row needs a part, a cut sheet/list, and a quantity. Rows missing a cut source can’t create jobs.
        </div>
      )}
    </div>
  );
}

function BulkCreationSummary({ createdJobs, customerList }) {
  function getCustomerName(cid) { const found = customerList.find(c => c.customer_id === cid); return found ? found.name : "Unknown Customer"; }
  return (
    <div style={{ marginTop: "2rem" }}><h4>Bulk Creation Summary</h4>
      <table className="table table-striped table-bordered">
        <thead><tr><th>Job Number</th><th>Customer</th><th>Customer PO</th><th>Cust SO</th><th>Parts on Job</th><th>Due Date</th><th>Priority</th></tr></thead>
        <tbody>{createdJobs.map((cj) => { const { job, parts } = cj; const custName = getCustomerName(job.customer_id);
            return (
              <tr key={job.job_id}><td>{job.master_job_num}</td><td>{custName}</td><td>{job.customer_po || ""}</td><td>{job.customer_so || ""}</td>
                <td>{parts.map((p) => (<div key={p.part_id}>{p.product_id} – {p.description} (Qty: {p.quantity_ordered})</div>))}</td>
                <td>{job.due_date || ""}</td><td>{job.priority_level}</td>
              </tr>);
        })}</tbody>
      </table>
    </div>
  );
}

function BulkPartsSummary({ bulkRows }) {
  const aggregatedParts = useMemo(() => {
    const summary = new Map();
    bulkRows.forEach(row => {
      if (Array.isArray(row.previewParts)) {
        row.previewParts.forEach(part => {
          if (summary.has(part.part_id)) {
            const existingPart = summary.get(part.part_id);
            existingPart.totalQuantity += part.totalQuantity;
          } else { summary.set(part.part_id, { ...part }); }
        });
      }
    });
    return Array.from(summary.values()).sort((a, b) => (a.product_id || '').localeCompare(b.product_id || ''));
  }, [bulkRows]);
  if (aggregatedParts.length === 0) return null;
  return (
    <div style={{ marginTop: "2rem", borderTop: "1px solid #ddd", paddingTop: "1rem" }}><h4>Overall Parts Summary</h4><p>Total quantities for all parts required across all jobs in this bulk entry.</p>
      <table className="table table-sm table-striped">
        <thead><tr><th>Product ID</th><th>Description</th><th>Total Quantity</th></tr></thead>
        <tbody>{aggregatedParts.map(part => (<tr key={part.part_id}><td>{part.product_id}</td><td>{part.description}</td><td>{Math.round(part.totalQuantity)}</td></tr>))}</tbody>
      </table>
    </div>
  );
}